import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDescriptionTasks } from "../../../../../store/slices/DescriptionTask";
import { fetchAllQuotations } from "../../../../../store/slices/generateQuotationSlice";
import { fetchHardwareDescriptions } from "../../../../../store/slices/hardwareDescriptionSlice";
import { fetchOtherExpenses } from "../../../../../store/slices/otherexpenses";
import { fetchSalesHourlyRates } from "../../../../../store/slices/salesHourlyRateSlice";
import { fetchSalesTaxes } from "../../../../../store/slices/salesTaxesSlice";
import { getMyTrainings } from "../../../../../store/slices/MyTraining/myTrainings";
import { replaceProjectFinanceNotes } from "../../../../../store/slices/projectFinanceNotesSlice";
import { useSnackbar } from "notistack";
// import { updateServiceValueByType } from "../../../../../store/slices/changeQuotedValueforServiceSlice";
import { updateSalesMargin } from "../../../../../store/slices/updateSalesMarginSlice";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import CommaConvert from "../../../../../utils/CommaConvert";
import {
  parseAmountNumber,
  formatAmountComma,
} from "../../../../../utils/decimalAmountUtils";
import { getInstallationQuotedTrainingDisplayAmount } from "../../../../../utils/trainingOtherExpenseSync";
import {
  getLoggedSalesUserId,
  getPerdiemAndAverageCostFromExpenses,
  scopeOtherExpensesForUser,
  scopeSalesHourlyRatesForUser,
} from "../../../../../utils/salespersonRatesScope";
import AdminLoader from "../../../../../components/common/loader/AdminLoader";
/** Won / quoted leads: hourly rates must stay on the snapshot, not live SP master rates. */
function isLeadRatesLocked(row) {
  if (row == null) return false;
  const status = String(row?.Status ?? row?.status ?? "").trim().toLowerCase();
  if (status === "won" || status === "won recurring") return true;
  if (typeof row?.isProjectQuoted === "boolean") return row.isProjectQuoted;
  if (row?.isProjectQuoted === 1) return true;
  if (String(row?.isProjectQuoted || "").toLowerCase() === "true") return true;
  return false;
}

const lockedPlHourlyRateCache = new Map();

function resolveLockedPlRate(quotationId, typeService, kind, snapshotRate) {
  const key = `${String(quotationId ?? "").trim()}::${String(typeService ?? "")
    .trim()
    .toLowerCase()}::${kind}`;
  if (lockedPlHourlyRateCache.has(key)) {
    return lockedPlHourlyRateCache.get(key);
  }
  const snap = Number(snapshotRate) || 0;
  if (snap > 0) {
    lockedPlHourlyRateCache.set(key, snap);
    return snap;
  }
  return 0;
}

function normalizePersonId(id) {
  if (id == null || id === "") return "";
  if (typeof id === "object") {
    return String(id._id ?? id.id ?? "").trim();
  }
  return String(id).trim();
}

/** Round margins to 2dp and adjust one key so the sum is exactly 100.00 (cost-proportion rounding drift). */
const normalizeMarginsSumTo100 = (marginByKey, orderedKeys, adjustKey) => {
  if (!orderedKeys?.length) return { ...(marginByKey || {}) };
  const round2 = (n) => Number((Number(n) || 0).toFixed(2));
  const out = {};
  orderedKeys.forEach((k) => {
    out[k] = round2(marginByKey?.[k] ?? 0);
  });
  const sumRounded = orderedKeys.reduce((s, k) => s + (out[k] || 0), 0);
  const diff = round2(100 - sumRounded);
  if (diff === 0) return out;
  const keyToAdjust = adjustKey || orderedKeys[orderedKeys.length - 1];
  out[keyToAdjust] = round2((out[keyToAdjust] || 0) + diff);
  return out;
};

const AllServiceTable = forwardRef(({
  handlerefresh,
  filteredQuotation,
  // showAssociatedCostTable,
  selectedProject,
  projects,
  calculatedProjects,
  handleAccociatedTabClick,
  setinstallationPersons,
  setSoftwarePersons,
  setCloudPersons,
  setHardwarePersons,
  tasks,
  items,
  onTableServiceTotalsChange,
  onTotalCostPerServiceTotalChange,
  onItemizedTotalsChange,
  projectLevelTotals,
  /** Wizard ERP: keep Expected Margin edits after save/refresh (avoid re-init from stale cost keys). */
  preserveMarginsOnRefresh = false,
  /** Wizard only: light data refresh after margin save (no full handlerefresh). */
  onWizardMarginsSaved,
}, ref) => {
  const dispatch = useDispatch();
  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();
  const formatMoneyComma = formatAmountComma;
  const round2 = (n) => Number((Number(n) || 0).toFixed(2));
  const formatHours = (value) =>
    new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(Number(value) || 0);
  const { enqueueSnackbar } = useSnackbar();
  // const { quotations } = useSelector((state) => state.hourlyRateQuotation);
  const { employees } = useSelector((state) => state.employees);
  const { mytrainings } = useSelector((state) => state.myTrainings);
  const { items: otherExpenses } = useSelector((state) => state.otherExpenses);
  const { data: salesHourlyRateData } = useSelector((state) => state.salesHourlyRate);
  const loggedInUserId = useSelector((state) => state.leads.loggedInUserId);
  const authLevel = useSelector(
    (state) =>
      state.leads.authLevel ||
      state.session?.auth ||
      (typeof localStorage !== "undefined" ? localStorage.getItem("auth") : "") ||
      (typeof localStorage !== "undefined" ? localStorage.getItem("userRole") : "")
  );
  const { taxes } = useSelector((state) => state.salesTaxes);
  const email = typeof localStorage !== "undefined" ? localStorage.getItem("email") : "";
  const loggedUserId = useMemo(
    () => getLoggedSalesUserId(loggedInUserId, employees, email),
    [loggedInUserId, employees, email]
  );
  const isL3User = String(authLevel || "").trim().toUpperCase() === "L3";
  const scopedSalesHourlyRates = useMemo(
    () =>
      scopeSalesHourlyRatesForUser(salesHourlyRateData, {
        loggedUserId,
        isL3User,
        onlyWhenL3: true,
      }),
    [salesHourlyRateData, isL3User, loggedUserId]
  );
  const [GandATax, setGandATax] = useState(15);
  const [taxValue, setTaxValue] = useState(27);
  const [perdiem, setperdiem] = useState(0);
  const [averagecost, setaveragecost] = useState(0);
  const [selected, setSelected] = useState(false);
  const [totalServiceGrandTotal, setTotalServiceGrandTotal] = useState(0);
  const tableContainerRef = useRef(null);
  const lastEmittedTotalCostPerServiceRef = useRef(null);
  const [totalGrossProfit, setTotalGrossProfit] = useState(0);
  const [totalGAExpenses, setTotalGAExpenses] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [totalNetRevenue, setTotalNetRevenue] = useState(0);
  const [netRevenuePercentage, setNetRevenuePercentage] = useState(0);
  const [totalHoursSum, setTotalHoursSum] = useState(0);
  const [saveTimeout, setSaveTimeout] = useState({});
  const [notes, setNotes] = useState({});
  const [quotedValues, setQuotedValues] = useState({});
  const [marginValues, setMarginValues] = useState({});
  const [editedMarginKeys, setEditedMarginKeys] = useState(new Set()); // Track which margins user has edited
  const [isLoading, SetisLoading] = useState(false);
  const [ratesReady, setRatesReady] = useState(false);
  const isBatchUpdatingMarginsRef = useRef(false);
  const lastMarginInitKeyRef = useRef("");
  const lastMarginRecalcCostSigRef = useRef("");
  const savedMarginsAfterBatchRef = useRef(null);
  const marginsLockedAfterSaveRef = useRef(false);
  const marginLockProjectRef = useRef("");
  const marginValuesRef = useRef({});
  // const [showMarginAlert, setShowMarginAlert] = useState(false);
  // Add these state variables near your other state declarations
  const [marginValidationTimer, setMarginValidationTimer] = useState(null);
  const [lastMarginAlertTime, setLastMarginAlertTime] = useState(null);
  const totalMarginPercentageRef = useRef(0);
  // const { projects } = useSelector((state) => state.projects);
  const [quotedproject, setquotedproject] = useState([]);

  useImperativeHandle(ref, () => ({
    collapseItemized: () => setSelected(false),
  }), []);

  useEffect(() => {
    dispatch(fetchDescriptionTasks());
    dispatch(fetchAllQuotations());
    dispatch(fetchHardwareDescriptions());
    dispatch(fetchOtherExpenses());
    dispatch(fetchSalesHourlyRates());
    dispatch(fetchSalesTaxes());
    dispatch(getMyTrainings());
    // dispatch(fetchAllProjects());

  }, [dispatch]);

  // Keep latest margin values available to effects without adding deps (avoids exhaustive-deps warning).
  useEffect(() => {
    marginValuesRef.current = marginValues || {};
  }, [marginValues]);

  // STEP 1 (Reset): force hourly rates/efforts to zero before any render/calculation
  useEffect(() => {
    console.log("STEP 1: Reset hourly rates to zero");
    lockedPlHourlyRateCache.clear();
    setRatesReady(false);
  }, [selectedProject?._id, selectedProject?.ProjectID, selectedProject?.ProjectName]);


  useEffect(() => {
    taxes?.forEach((data) => {
      if (data.taxName === "G&A Expenses") {
        setGandATax(data.taxPercentage);
      } else if (data.taxName === "Tax") {
        setTaxValue(data.taxPercentage);
      }
    });
  }, [taxes]);

  useEffect(() => {
    if (!selectedProject?.ProjectName) {
      setquotedproject([]);
      return;
    }
    // Use quotationId first: when selected has quotationId, match only by quotationId (unique); else fall back to ProjectName
    const finalproject = projects.filter((proj) => {
      if (proj.isProjecttracking === true || proj.isProjectQuoted !== true) return false;
      const hasQuotationId = selectedProject.quotationId != null && String(selectedProject.quotationId).trim() !== "";
      if (hasQuotationId) {
        return proj.quotationId != null && String(proj.quotationId).trim() === String(selectedProject.quotationId).trim();
      }
      return proj.ProjectName === selectedProject.ProjectName;
    });
    setquotedproject(finalproject);
  }, [projects, selectedProject]);

  // Routing/display mode: only backend isProjectQuoted (do not fold Won into this — avoids duplicate rows).
  const isProjectQuoted = useMemo(() => {
    if (typeof selectedProject?.isProjectQuoted === "boolean") return selectedProject.isProjectQuoted;
    if (selectedProject?.isProjectQuoted === 1) return true;
    if (String(selectedProject?.isProjectQuoted || "").toLowerCase() === "true") return true;
    return false;
  }, [selectedProject?.isProjectQuoted]);

  const ratesLocked = useMemo(
    () => isLeadRatesLocked(selectedProject),
    [selectedProject]
  );

  const findInstallationProjectSnapshot = useCallback(
    (projectID) => {
      if (projectID) {
        const fromQuoted = (quotedproject || []).find(
          (p) => String(p._id) === String(projectID)
        );
        if (fromQuoted) return fromQuoted;
        const fromProjects = (projects || []).find(
          (p) => String(p._id) === String(projectID)
        );
        if (fromProjects) return fromProjects;
      }

      const selQId =
        selectedProject?.quotationId != null &&
        String(selectedProject.quotationId).trim() !== ""
          ? String(selectedProject.quotationId).trim()
          : "";
      const selName = String(selectedProject?.ProjectName || "").trim();

      return (
        (projects || []).find((p) => {
          const ts = String(p?.TypeService || "").toLowerCase();
          if (!ts.includes("installation")) return false;
          if (
            selQId &&
            p.quotationId != null &&
            String(p.quotationId).trim() === selQId
          ) {
            return true;
          }
          return selName && String(p.ProjectName || "").trim() === selName;
        }) ||
        (quotedproject || []).find((p) =>
          String(p?.TypeService || "").toLowerCase().includes("installation")
        )
      );
    },
    [quotedproject, projects, selectedProject]
  );

  /** Won / quoted: freeze perdiem + avgcost from project snapshot; else live SP settings. */
  const resolveInstallationPerdiemAndAverageCost = useCallback(
    (projectID) => {
      if (ratesLocked || isProjectQuoted) {
        const snapshotProject = findInstallationProjectSnapshot(projectID);
        if (snapshotProject) {
          return {
            perdiem: Number(snapshotProject.perdiem) || 0,
            averagecost:
              Number(
                snapshotProject.avgcost ?? snapshotProject.averagecost
              ) || 0,
          };
        }
      }
      return { perdiem, averagecost };
    },
    [
      ratesLocked,
      isProjectQuoted,
      findInstallationProjectSnapshot,
      perdiem,
      averagecost,
    ]
  );

  // STEP 2: Mark salesperson hourly rates ready for live (non-quoted) calculations
  useEffect(() => {
    if (isProjectQuoted || ratesLocked) {
      setRatesReady(true);
      return;
    }

    console.log("SP Rates:", scopedSalesHourlyRates);
    setRatesReady(true);
  }, [
    scopedSalesHourlyRates,
    isProjectQuoted,
    ratesLocked,
    selectedProject?._id,
    selectedProject?.ProjectID,
    selectedProject?.ProjectName,
    selectedProject?.quotationId,
  ]);


  useEffect(() => {
    const expensesForUser = scopeOtherExpensesForUser(otherExpenses, {
      loggedUserId,
      isL3User,
      onlyWhenL3: true,
    });
    const { perdiem: p, averagecost: a } =
      getPerdiemAndAverageCostFromExpenses(expensesForUser);
    setperdiem(p);
    setaveragecost(a);
  }, [otherExpenses, isL3User, loggedUserId]);

  const getDescriptionValues = useCallback(
    (projectID, personID, typeService) => {
      if (!tasks) return [];
      const isTrainingDescriptionRow = (desc) => {
        const valuesToCheck = [
          desc?.task,
          desc?.Task,
          desc?.description,
          desc?.Description,
          desc?.title,
          desc?.name,
          desc?.category,
          desc?.type,
          desc?.expenseType,
          desc?.costType,
          desc?.head,
          desc?.label,
        ];
        return valuesToCheck.some((value) =>
          String(value || "").trim().toLowerCase().includes("training")
        );
      };
      const normalizedProjectId = String(projectID || "").trim();
      const normalizedPersonId = normalizePersonId(personID);
      return tasks.filter(
        (desc) => {
          const descProjectId = String(desc?.projectid || "").trim();
          const descTaskOwnerId = normalizePersonId(desc?.taskownerid);
          if (!(descProjectId === normalizedProjectId && descTaskOwnerId === normalizedPersonId)) return false;
          if (isTrainingDescriptionRow(desc)) return false;
          // Keep backward compatibility for old description rows without a type.
          if (!typeService) return true;
          const descTypeService = String(desc?.typeservice || "").trim().toLowerCase();
          if (!descTypeService) return true;
          return descTypeService === String(typeService).trim().toLowerCase();
        }
      );
    },
    [tasks]
  );

  // const getTotalDescriptionValue = useCallback(
  //   (projectID, personID, typeService) => {
  //     const descriptions = getDescriptionValues(
  //       projectID,
  //       personID,
  //       typeService
  //     );

  //     let totalValue = descriptions.reduce(
  //       (sum, desc) => sum + (desc.value || 0),
  //       0
  //     );

  //     // Check if this is an Installation service
  //     if (typeService && typeService.toLowerCase().includes("installation")) {
  //       // Find the maximum trip duration for this person (to avoid duplicate counting)
  //       let maxTripDuration = 0;
  //       const uniqueTripDurations = new Set();

  //       descriptions.forEach((desc) => {
  //         if (desc.tripduration) {
  //           uniqueTripDurations.add(desc.tripduration);
  //           maxTripDuration = Math.max(maxTripDuration, desc.tripduration);
  //         }
  //       });

  //       // Use the maximum trip duration for calculation
  //       // If you want to use average trip duration instead, you can calculate:
  //       // const avgTripDuration = Array.from(uniqueTripDurations).reduce((sum, duration) => sum + duration, 0) / uniqueTripDurations.size;

  //       if (maxTripDuration > 0) {
  //         // Add: (tripduration * perdiem) + (tripduration * averageCost)
  //         totalValue +=
  //           maxTripDuration * perdiem + maxTripDuration * averagecost;
  //       }
  //     }

  //     return totalValue;
  //   },
  //   [getDescriptionValues, perdiem, averagecost] // Add perdiem and averageCost to dependencies
  // );
  const getTotalDescriptionValue = useCallback(
    (projectID, personID, typeService) => {
      const descriptions = getDescriptionValues(
        projectID,
        personID,
        typeService
      );
      let totalValue = descriptions.reduce(
        (sum, desc) => sum + parseAmountNumber(desc?.value),
        0
      );

      // Include Training cost rows into Associated Cost totals.
      // We intentionally exclude them from `getDescriptionValues()` so they don't mix with normal expenses rows,
      // but they MUST contribute to Associated Cost and all downstream totals.
      if (tasks && projectID && personID) {
        const isTrainingDescriptionRow = (desc) => {
          const valuesToCheck = [
            desc?.task,
            desc?.Task,
            desc?.description,
            desc?.Description,
            desc?.title,
            desc?.name,
            desc?.category,
            desc?.type,
            desc?.expenseType,
            desc?.costType,
            desc?.head,
            desc?.label,
          ];
          return valuesToCheck.some((value) =>
            String(value || "").trim().toLowerCase().includes("training")
          );
        };
        const normalizedProjectId = String(projectID || "").trim();
        const normalizedPersonId = normalizePersonId(personID);
        const normalizedTypeService = String(typeService || "").trim().toLowerCase();

        let trainingTotal = 0;
        if (normalizedTypeService.includes("installation")) {
          const assigneeEmp = (employees || []).find(
            (e) => String(e?._id || "").trim() === normalizedPersonId
          );
          const installationEntries = tasks.filter((desc) => {
            const descProjectId = String(desc?.projectid || "").trim();
            const descTaskOwnerId = normalizePersonId(desc?.taskownerid);
            if (!(descProjectId === normalizedProjectId && descTaskOwnerId === normalizedPersonId)) {
              return false;
            }
            const dst = String(desc?.typeservice || "").trim().toLowerCase();
            if (!dst) return true;
            return dst.includes("installation");
          });
          const instProjects = ([]).concat(projects || []).filter((p) => {
            const ts = p?.TypeService;
            if (!ts) return false;
            if (Array.isArray(ts)) {
              return ts.some((x) => String(x || "").toLowerCase().includes("installation"));
            }
            return String(ts).toLowerCase().includes("installation");
          });
          trainingTotal = getInstallationQuotedTrainingDisplayAmount({
            entries: installationEntries,
            matchedProjects: instProjects.length ? instProjects : ([]).concat(projects || []),
            assigneeEmp,
            trainingsEnvelope: mytrainings,
            projects: projects || [],
            currencySymbol: currency,
          });
        } else {
          trainingTotal = tasks
            .filter((desc) => {
              const descProjectId = String(desc?.projectid || "").trim();
              const descTaskOwnerId = normalizePersonId(desc?.taskownerid);
              if (!(descProjectId === normalizedProjectId && descTaskOwnerId === normalizedPersonId)) {
                return false;
              }
              if (!isTrainingDescriptionRow(desc)) return false;
              if (!normalizedTypeService) return true;
              const descTypeService = String(desc?.typeservice || "").trim().toLowerCase();
              if (!descTypeService) return true;
              return descTypeService === normalizedTypeService;
            })
            .reduce((sum, desc) => sum + parseAmountNumber(desc?.value), 0);
        }

        totalValue += trainingTotal;
      }

      // Check if this is an Installation service
      if (typeService && typeService.toLowerCase().includes("installation")) {
        // Find the maximum trip duration for this person (to avoid duplicate counting)
        let maxTripDuration = 0;
        const uniqueTripDurations = new Set();

        descriptions.forEach((desc) => {
          if (desc.tripduration) {
            uniqueTripDurations.add(desc.tripduration);
            maxTripDuration = Math.max(maxTripDuration, desc.tripduration);
          }
        });

        if (maxTripDuration > 0) {
          const { perdiem: instPerdiem, averagecost: instAvgcost } =
            resolveInstallationPerdiemAndAverageCost(projectID);
          totalValue += maxTripDuration * instPerdiem + maxTripDuration * instAvgcost;
        }
      }

      // IMPORTANT: If it's a hardware service, return 0 for non-manager employees
      // This will allow us to aggregate descriptionTotal for hardware services
      if (typeService && typeService.toLowerCase().includes("hardware")) {
        // We'll return 0 here, and handle the aggregation separately in the transformFilteredQuotation function
        return totalValue; // But we need to decide based on role - will be handled later
      }

      return totalValue;
    },
    [
      getDescriptionValues,
      resolveInstallationPerdiemAndAverageCost,
      tasks,
      employees,
      projects,
      mytrainings,
      currency,
    ]
  );

  const getHardwareValues = useCallback(
    (projectID, typeService) => {
      if (!items) return [];
      return items.filter(
        (desc) =>
          desc.projectid === projectID &&
          // desc.taskownerid === personID &&
          desc.typeservice === typeService
      );
    },
    [items]
  );

  const getTotalHardwareCostValue = useCallback(
    (projectID, personID, typeService) => {
      let hardwareValue = 0;
      const hardwareCost = getHardwareValues(projectID, typeService);
      const hardwareByDescription = {};

      // Group hardware by assemblepartdescription and keep only the one with highest totalprice
      hardwareCost.forEach((hw) => {
        const description = hw.assemblepartdescription;

        if (!hardwareByDescription[description]) {
          hardwareByDescription[description] = hw;
        } else {
          // If we already have this description, keep the one with higher totalprice
          if (hw.totalprice > hardwareByDescription[description].totalprice) {
            hardwareByDescription[description] = hw;
          }
        }
      });

      // Calculate total hardware value using only the highest totalprice items
      Object.values(hardwareByDescription).forEach((hw) => {
        if (hw.totalprice) {
          hardwareValue += hw.totalprice;
        }
      });
      return hardwareValue;
      // return hardwareCost.reduce((sum, desc) => sum + (desc.totalprice || 0), 0);
    },
    [getHardwareValues]
  );

  const [groupedByPersonState, setGroupedByPersonState] = useState([]);
  const [updatedGroupedByPerson, setUpdatedGroupedByPerson] = useState([]);
  const [finalData, setFinalData] = useState([]);
  const [allProjectsOnly, setAllProjectsOnly] = useState([]);
  const [matchedDescription, setMatchedDescription] = useState(0);
  const [matchedHardware, setMatchedHardware] = useState(0);
  const [totalCostSum, setTotalCostSum] = useState("");




  useEffect(() => {
    if (!filteredQuotation) return;

    const newGrouped = {};

    Object.entries(filteredQuotation).forEach(([projectName, typeServices]) => {
      Object.entries(typeServices).forEach(([typeService, items]) => {
        items.forEach((item) => {
          if (!item?.WorkEntries || item.WorkEntries.length === 0) return;

          item.WorkEntries.forEach((entry) => {
            const personName = entry?.createdBy?.name;
            const personId = entry?.taskownerid;
            const personRoles = entry?.createdBy?.role[0] || [];
            const matchedEmp = employees.find((emp) => emp._id === personId);

            let hourlyRate = 0;
            if (item?.quotationDetails?.length > 0) {
              const matchedDetail = item.quotationDetails.find((qd) => {
                const designation = qd.designation?.toLowerCase() || "";
                const typeServiceStr = item.TypeService?.toLowerCase() || "";
                const empService = matchedEmp?.serviceType?.toLowerCase() || "";

                if (!designation.includes(empService)) return false;
                if (typeServiceStr && !designation.includes(typeServiceStr))
                  return false;

                return true;
              });

              if (matchedDetail) {
                hourlyRate = matchedDetail.hourlysalaray || 0;
              }
            }

            if (!newGrouped[personName]) {
              newGrouped[personName] = {
                name: personName,
                totalHours: 0,
                personId,
                roles: personRoles,
                projects: [],
              };
            }

            newGrouped[personName].totalHours += entry.hoursassigned || 0;
            newGrouped[personName].projects.push({
              projectId: item.ProjectID,
              typeService: item.TypeService,
              hourlyRate,
            });
          });
        });
      });
    });

    const groupedArray = Object.values(newGrouped);
    setGroupedByPersonState(groupedArray);
  }, [filteredQuotation, employees]);

  // Other useEffect hooks remain unchanged
  useEffect(() => {
    if (groupedByPersonState?.length && employees?.length) {
      const updated = groupedByPersonState.map((person) => {
        const matchedEmp = employees.find((emp) => emp._id === person.personId);

        if (matchedEmp) {
          return {
            ...person,
            designation:
              matchedEmp.servicetype + " " + matchedEmp.customRoles[0],
          };
        }
        return person;
      });

      setUpdatedGroupedByPerson(updated);
    }
  }, [groupedByPersonState, employees]);

  // NOTE: Hourly rates must come from backend payload only (no master-rate substitution).
  // This block used to enrich data from master hourly rates; it's intentionally disabled.
  useEffect(() => {
    if (updatedGroupedByPerson?.length && employees?.length) {
      const updated = updatedGroupedByPerson.map((person) => {
        const matchedEmp = employees.find((emp) => emp._id === person.personId);
        return {
          ...person,
          TypeService: matchedEmp?.servicetype || null,
        };
      });

      setFinalData(updated);
    }
  }, [updatedGroupedByPerson, employees]);

  useEffect(() => {
    if (finalData?.length) {
      const projectsArray = finalData.flatMap(
        (person) => person.projects || []
      );
      const projectIds = projectsArray.map((project) => project.projectId);

      setAllProjectsOnly(projectIds);
    }
  }, [finalData]);



  useEffect(() => {
    if (allProjectsOnly?.length && items?.length) {
      const matchedHardwareItems = items.filter((item) =>
        allProjectsOnly.includes(item.projectid)
      );

      // Group hardware by assemblepartdescription and keep only the one with highest totalprice
      const hardwareByDescription = {};
      matchedHardwareItems.forEach((hw) => {
        const description = hw.assemblepartdescription;

        if (!hardwareByDescription[description]) {
          hardwareByDescription[description] = hw;
        } else {
          // If we already have this description, keep the one with higher totalprice
          if (hw.totalprice > hardwareByDescription[description].totalprice) {
            hardwareByDescription[description] = hw;
          }
        }
      });

      // Calculate total hardware value using only the highest totalprice items
      const totalHardwareValue = Object.values(hardwareByDescription).reduce(
        (sum, hw) => sum + (hw.totalprice || 0),
        0
      );

      setMatchedHardware(totalHardwareValue);
    }
  }, [allProjectsOnly, items]);

  // const calculateGrossProfit = (
  //   projectName,
  //   serviceGrandTotal,
  //   typeService,
  //   projectID
  // ) => {
  //   const serviceKey = `${projectID}-${typeService}`;
  //   const quotedValue =
  //     quotedValues[serviceKey] !== undefined
  //       ? parseFloat(quotedValues[serviceKey]) || 0
  //       : selectedProject.QuotedValue / Object.keys(groupedData).length || 0;
  //   const grossProfit = quotedValue - serviceGrandTotal;
  //   return grossProfit;
  // };



  const handleClick = () => {
    setSelected(!selected);
  };

  // const transformFilteredQuotation = useCallback(
  //   (filteredQuotation, employees) => {
  //     const result = [];

  //     Object.entries(filteredQuotation || {}).forEach(
  //       ([projectName, typeServices]) => {
  //         Object.entries(typeServices).forEach(([typeService, items]) => {
  //           items.forEach((item) => {
  //             if (!item?.WorkEntries?.length) return;

  //             // Group work entries by person
  //             const groupedByPerson = {};
  //             const personRoleMap = {};

  //             item.WorkEntries.forEach((entry) => {
  //               const personName = entry?.createdBy?.name;
  //               const personRoles = entry?.createdBy?.role || [];
  //               const personId = entry?.taskownerid;

  //               if (!groupedByPerson[personName]) {
  //                 groupedByPerson[personName] = { totalHours: 0, personId };
  //               }
  //               groupedByPerson[personName].totalHours +=
  //                 entry.hoursassigned || 0;

  //               if (!personRoleMap[personName]) {
  //                 personRoleMap[personName] = { roles: personRoles, personId };
  //               }
  //             });



  //             // Convert grouped persons into array with costs
  //             Object.entries(groupedByPerson).forEach(([name, personData]) => {
  //               const totalHours = personData.totalHours;
  //               const personId = personData.personId;
  //               const ProjectID = item?.ProjectID;
  //               const ProjectNotes = item?.ProjectNotes;
  //               const ServiceQuotedValue = item?.QuotedValue || 0;
  //               const servicewiseMargin = item?.ProjectMargin || 0;

  //               // Get roles for this person
  //               const personRoleInfo = personRoleMap[name];
  //               const personRoles = personRoleInfo?.roles || [];



  //               // Find matching quotation based on roles
  //               let matchedQuotation = null;

  //               if (item.quotationDetails) {
  //                 // Try to match by checking if any role matches the designation
  //                 // Since personRoles is an array and designation is a string, we need to check each role
  //                 for (const role of personRoles) {
  //                   matchedQuotation = item.quotationDetails.find(
  //                     (q) => q.designation.toLowerCase() === role.toLowerCase()
  //                   );

  //                   if (matchedQuotation) {

  //                     break;
  //                   }
  //                 }

  //                 // If no match found with exact role, try partial matching
  //                 if (!matchedQuotation) {
  //                   for (const role of personRoles) {
  //                     matchedQuotation = item.quotationDetails.find(
  //                       (q) =>
  //                         q.designation
  //                           .toLowerCase()
  //                           .includes(role.toLowerCase()) ||
  //                         role
  //                           .toLowerCase()
  //                           .includes(q.designation.toLowerCase())
  //                     );

  //                     if (matchedQuotation) {

  //                       break;
  //                     }
  //                   }
  //                 }
  //               }


  //               const currentEmployee = employees.find((emp) => {
  //                 const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`
  //                   .trim()
  //                   .toLowerCase();
  //                 return fullName === name.trim().toLowerCase();
  //               });

  //               const capitalizedRoles =
  //                 currentEmployee?.customRoles?.map(
  //                   (role) =>
  //                     role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
  //                 ) || [];

  //               // Use matched quotation hourly rate, fallback to 0 if not found
  //               const hourlyRate = matchedQuotation
  //                 ? (matchedQuotation?.hourlysalaray || 0) +
  //                 (matchedQuotation?.overheadpercentage || 0)
  //                 : 0;

  //               const totalCost = totalHours * hourlyRate;

  //               // External helpers
  //               const descriptionTotal = getTotalDescriptionValue(
  //                 item.ProjectID,
  //                 personId,
  //                 item.TypeService
  //               );

  //               const hardwareTotal = getTotalHardwareCostValue(
  //                 item.ProjectID,
  //                 personId,
  //                 item.TypeService
  //               );

  //               const hasProjectManagerRole =
  //                 capitalizedRoles.includes("Project manager");
  //               result.push({
  //                 servicewiseMargin,
  //                 ServiceQuotedValue,
  //                 projectName,
  //                 typeService: item.TypeService,
  //                 resourceType: item.ResourceType,
  //                 personId,
  //                 ProjectID,
  //                 ProjectNotes,
  //                 personName: name,
  //                 roles: capitalizedRoles,
  //                 totalHours,
  //                 hourlyRate,
  //                 totalCost,
  //                 descriptionTotal,
  //                 hardwareTotal,
  //                 grandTotal: item?.TypeService?.toLowerCase().includes(
  //                   "hardware"
  //                 )
  //                   ? (hasProjectManagerRole ? hardwareTotal : 0) +
  //                   descriptionTotal
  //                   : descriptionTotal + totalCost,
  //                 matchedDesignation:
  //                   matchedQuotation?.designation || "Not matched",
  //               });
  //             });
  //           });
  //         });
  //       }
  //     );

  //     return result;
  //   },
  //   [getTotalDescriptionValue, getTotalHardwareCostValue]
  // );


  const transformFilteredQuotation = useCallback(
    (filteredQuotation, employees, projectsData) => {
      const result = [];

      Object.entries(filteredQuotation || {}).forEach(
        ([projectName, typeServices]) => {
          Object.entries(typeServices).forEach(([typeService, items]) => {
            if (!items?.length) return;
            // Merge all project rows for this service so hours + associated costs stay complete without duplicate rows.
            const item = {
              ...items[0],
              WorkEntries: items.flatMap((i) => i?.WorkEntries || []),
              Assignedperson: items.flatMap((i) => i?.Assignedperson || []),
            };
            const projectIdsForService = [
              ...new Set(items.map((i) => i?.ProjectID).filter(Boolean)),
            ];

            {
              const ProjectID = item?.ProjectID;
              const ProjectNotes = item?.ProjectNotes;
              const ServiceQuotedValue = item?.QuotedValue || 0;
              const servicewiseMargin = item?.ProjectMargin || 0;
              const isHardwareService = item?.TypeService?.toLowerCase().includes("hardware");
              const quotedProjectData =
                projectIdsForService
                  .map((pid) => quotedproject?.find((qp) => String(qp._id) === String(pid)))
                  .find(Boolean) ||
                quotedproject?.find((qp) => String(qp._id) === String(ProjectID)) ||
                projectIdsForService
                  .map((pid) =>
                    projects?.find(
                      (p) =>
                        String(p._id) === String(pid) && p.isProjecttracking === false
                    )
                  )
                  .find(Boolean) ||
                projects?.find(
                  (p) =>
                    String(p._id) === String(ProjectID) &&
                    p.isProjecttracking === false
                );
              const quotationIdForLock =
                item?.quotationId ?? selectedProject?.quotationId ?? projectName;
              const normalizedService = String(typeService || item?.TypeService || "")
                .trim()
                .toLowerCase();
              const safeNum = (v) => (v == null ? 0 : Number(v) || 0);
              const computeQuotedDetailRate = (rateRow) =>
                round2(
                  safeNum(rateRow?.hourlysalaray ?? rateRow?.hourlySalary) +
                    safeNum(rateRow?.overheadpercentage ?? rateRow?.overhead)
                );
              const findSnapshotRate = (kind) => {
                const field =
                  kind === "manager" ? "managerHourlyRate" : "operationHourlyRate";
                for (const src of items) {
                  const v = safeNum(src?.[field]);
                  if (v > 0) return v;
                }
                for (const pid of projectIdsForService) {
                  const row =
                    quotedproject?.find((qp) => String(qp._id) === String(pid)) ||
                    projects?.find((p) => String(p._id) === String(pid));
                  const v = safeNum(row?.[field]);
                  if (v > 0) return v;
                }
                return safeNum(quotedProjectData?.[field]);
              };
              const pickRateFromQuotationDetails = (kind) => {
                const isManagerKind = kind === "manager";
                const isOpsKind = kind === "operations";
                for (const src of items) {
                  const details = Array.isArray(src?.quotationDetails)
                    ? src.quotationDetails
                    : [];
                  const detailMatch = details.find((d) => {
                    const desig = String(d?.designation || "").toLowerCase();
                    if (!desig) return false;
                    if (normalizedService && !desig.includes(normalizedService)) {
                      return false;
                    }
                    if (isManagerKind) {
                      return desig.includes("manager") || desig.includes("pm");
                    }
                    if (isOpsKind) return desig.includes("operation");
                    return false;
                  });
                  if (detailMatch) {
                    const v = computeQuotedDetailRate(detailMatch);
                    if (v > 0) return v;
                  }
                }
                return 0;
              };
              const pickRateByQuotedFlag = (kind) => {
                if (!ratesLocked && !isProjectQuoted) return 0;
                const snapshotRate = findSnapshotRate(kind);
                const locked = resolveLockedPlRate(
                  quotationIdForLock,
                  typeService || item?.TypeService,
                  kind,
                  snapshotRate
                );
                if (locked > 0) return locked;
                return pickRateFromQuotationDetails(kind);
              };
              const managerHourlyRate = pickRateByQuotedFlag("manager");
              const operationHourlyRate = pickRateByQuotedFlag("operations");

              // Step 1: Get all persons from Assignedperson array
              const allAssignedPersons = item?.Assignedperson || [];
              const assignedPersonMap = new Map();

              // First, map all assigned persons by email for easy lookup
              allAssignedPersons.forEach((person) => {
                if (person?.email) {
                  assignedPersonMap.set(person.email.toLowerCase(), {
                    name: person.name,
                    email: person.email,
                    personid: person.personid || person._id
                  });
                }
              });

              // Step 2: Get employees by matching emails
              const matchedEmployees = employees.filter(emp => {
                return assignedPersonMap.has(emp.EmpEmail?.toLowerCase());
              });

              // If no employees found by email, try to match by name as fallback
              if (matchedEmployees.length === 0) {
                allAssignedPersons.forEach((person) => {
                  const matchedEmployee = employees.find(emp => {
                    const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`.trim().toLowerCase();
                    return fullName === person.name?.trim().toLowerCase();
                  });

                  if (matchedEmployee) {
                    matchedEmployees.push(matchedEmployee);
                  }
                });
              }

              // Step 3: Group work entries by person
              const workEntriesByPerson = {};
              const personWorkEntryData = {};

              if (item?.WorkEntries?.length) {
                item.WorkEntries.forEach((entry) => {
                  const personName = entry?.createdBy?.name;
                  const personRoles = entry?.createdBy?.role || [];
                  const personId = normalizePersonId(entry?.taskownerid);
                  const personEmail = entry?.createdBy?.email;

                  if (personName) {
                    if (!workEntriesByPerson[personName]) {
                      workEntriesByPerson[personName] = { totalHours: 0, personId };
                      personWorkEntryData[personName] = {
                        roles: personRoles,
                        email: personEmail
                      };
                    }
                    workEntriesByPerson[personName].totalHours += entry.hoursassigned || 0;
                  }
                });
              }

              // Step 4: Combine data from assigned persons and work entries
              // We need to process ALL assigned persons, not just those with work entries
              const allPersonsToProcess = [];

              // Add persons with work entries first
              Object.entries(workEntriesByPerson).forEach(([name, data]) => {
                const fallbackEmp = employees.find((e) => String(e?._id ?? "") === String(data.personId ?? ""));
                const fallbackEmail = fallbackEmp?.EmpEmail || "";
                allPersonsToProcess.push({
                  name,
                  personId: data.personId,
                  totalHours: data.totalHours,
                  roles: personWorkEntryData[name]?.roles || [],
                  email: personWorkEntryData[name]?.email || fallbackEmail,
                  hasWorkEntries: true
                });
              });

              // Add assigned persons who don't have work entries (e.g. managers with associated costs only).
              allAssignedPersons.forEach((person) => {
                const personName = person.name;
                const personEmail = person.email;

                const alreadyExists = allPersonsToProcess.some(
                  (p) => p.name === personName || p.email === personEmail
                );

                if (!alreadyExists && personName) {
                  const matchedEmployee = employees.find((emp) =>
                    emp.EmpEmail?.toLowerCase() === personEmail?.toLowerCase() ||
                    `${emp.EmpFirstName} ${emp.EmpLastName}`.trim().toLowerCase() === personName.toLowerCase()
                  );

                  allPersonsToProcess.push({
                    name: personName,
                    personId: normalizePersonId(matchedEmployee?._id || person.personid),
                    totalHours: 0,
                    roles: matchedEmployee?.customRoles || [],
                    email: personEmail,
                    hasWorkEntries: false,
                  });
                }
              });

              // HARD FIX: de-duplicate people (work entries + assigned persons) so managers/ops never appear twice.
              // Key priority: email -> personId -> normalized name.
              const dedupedPersonsMap = new Map();
              allPersonsToProcess.forEach((p) => {
                const key =
                  (p?.email ? String(p.email).trim().toLowerCase() : "") ||
                  (p?.personId ? String(p.personId).trim() : "") ||
                  (p?.name ? String(p.name).trim().toLowerCase() : "");
                if (!key) return;
                const existing = dedupedPersonsMap.get(key);
                if (!existing) {
                  dedupedPersonsMap.set(key, { ...p });
                  return;
                }
                // Merge hours + roles
                existing.totalHours = (Number(existing.totalHours) || 0) + (Number(p.totalHours) || 0);
                const roleSet = new Set([...(existing.roles || []), ...(p.roles || [])].map((r) => String(r || "").trim()).filter(Boolean));
                existing.roles = Array.from(roleSet);
                existing.hasWorkEntries = Boolean(existing.hasWorkEntries || p.hasWorkEntries);
                // Prefer a defined personId/email
                if (!existing.personId && p.personId) existing.personId = p.personId;
                if (!existing.email && p.email) existing.email = p.email;
                if (!existing.name && p.name) existing.name = p.name;
              });
              const uniquePersons = Array.from(dedupedPersonsMap.values());

              // Step 5: Check if any person is a manager
              let hasManager = false;
              uniquePersons.forEach((person) => {
                const isManager = person.roles.some(role =>
                  role.toLowerCase().includes('manager') ||
                  role.toLowerCase().includes('pm')
                );
                if (isManager) hasManager = true;
              });

              // Step 6: Track hardware cost assignment
              let hardwareCostAssigned = false;

              // Step 7: Sort persons: managers first, then others
              const sortedPersons = [...uniquePersons].sort((a, b) => {
                const isManagerA = a.roles.some(role =>
                  role.toLowerCase().includes('manager') ||
                  role.toLowerCase().includes('pm')
                );
                const isManagerB = b.roles.some(role =>
                  role.toLowerCase().includes('manager') ||
                  role.toLowerCase().includes('pm')
                );

                if (isManagerA && !isManagerB) return -1;
                if (!isManagerA && isManagerB) return 1;
                return 0;
              });

              // Step 8: Process each person
              sortedPersons.forEach((person, index) => {
                const { name, personId, totalHours, roles, email } = person;
                const isManager = roles.some(role =>
                  role.toLowerCase().includes('manager') ||
                  role.toLowerCase().includes('pm')
                );
                const isOperations = roles.some(role =>
                  role.toLowerCase().includes('operations')
                );

                // Find matching quotation for designation (used for matchedDesignation)
                let matchedQuotation = null;
                if (item?.quotationDetails?.length) {
                  // Try to match by roles
                  for (const role of roles) {
                    matchedQuotation = item.quotationDetails.find(q =>
                      q.designation?.toLowerCase() === role.toLowerCase()
                    );
                    if (matchedQuotation) break;
                  }

                  // Try partial matching if no exact match
                  if (!matchedQuotation) {
                    for (const role of roles) {
                      matchedQuotation = item.quotationDetails.find(q =>
                        q.designation?.toLowerCase().includes(role.toLowerCase()) ||
                        role.toLowerCase().includes(q.designation?.toLowerCase())
                      );
                      if (matchedQuotation) break;
                    }
                  }
                }

                const hourlyRate = (() => {
                  // HARD BLOCK: do not use any precomputed/stale values until we are ready
                  if (!ratesReady) return 0;

                  // LOCKED (won/quoted): snapshot rates only — not live SP master rates
                  if (ratesLocked || isProjectQuoted) {
                    if (isManager) return managerHourlyRate;
                    if (isOperations) return operationHourlyRate;
                    return 0;
                  }

                  // LIVE SP settings path: salary + overhead from logged-in salesperson rates (preserve decimals)
                  const typeNorm = String(item?.TypeService || "")
                    .toLowerCase()
                    .trim();
                  const normalizeDes = (s) =>
                    String(s || "").toLowerCase().trim().replace(/\s+/g, " ");
                  const pickRateRow = (predicate) =>
                    (scopedSalesHourlyRates || []).find((q) => {
                      const des = normalizeDes(q?.designation);
                      if (!des || (typeNorm && !des.includes(typeNorm))) return false;
                      return predicate(des);
                    });
                  const rateRow = isManager
                    ? pickRateRow(
                        (des) =>
                          des.includes("project manager") ||
                          (des.includes("manager") && !des.includes("operations"))
                      )
                    : isOperations
                    ? pickRateRow(
                        (des) =>
                          des.includes("operations") && !des.includes("manager")
                      )
                    : null;
                  const salary = Number(rateRow?.hourlysalaray ?? rateRow?.hourlySalary ?? 0) || 0;
                  const overhead =
                    Number(rateRow?.overheadpercentage ?? rateRow?.overhead ?? 0) || 0;
                  let rate = rateRow ? round2(salary + overhead) : 0;

                  // STRICT BLOCKER: never allow known stale values to reach UI unless they are truly from SP settings mapping
                  const staleCandidates = [600, 360, 96];
                  if (staleCandidates.includes(rate)) {
                    console.error("❌ STALE VALUE DETECTED:", rate, { typeNorm, isManager, isOperations });
                    rate = 0;
                  }
                  console.log(
                    "Mapping:",
                    `${item?.TypeService || ""} ${isManager ? "Manager" : "Employee"}`,
                    "→",
                    rateRow?.designation,
                    "→",
                    rate
                  );
                  return rate;
                })();

                const effectiveHourlyRate = ratesReady
                  ? round2(parseFloat(hourlyRate) || 0)
                  : 0;

                const totalCost = ratesReady
                  ? round2(parseFloat(totalHours) * effectiveHourlyRate)
                  : 0;

                // Get description and hardware totals (sum across all project rows for this service)
                const normalizedPersonId = normalizePersonId(personId);
                const descriptionTotal = projectIdsForService.reduce(
                  (sum, pid) =>
                    sum +
                    getTotalDescriptionValue(pid, normalizedPersonId, item.TypeService),
                  0
                );

                const hardwareTotal = projectIdsForService.reduce(
                  (sum, pid) =>
                    sum +
                    getTotalHardwareCostValue(pid, normalizedPersonId, item.TypeService),
                  0
                );

                // Calculate grand total based on service type and role
                let grandTotal = 0;

                if (isHardwareService) {
                  if (isManager) {
                    // Manager in hardware service: hardware + description + cost
                    grandTotal = hardwareTotal + descriptionTotal + totalCost;
                  } else if (isOperations) {
                    // Operations in hardware service without manager
                    if (!hasManager && !hardwareCostAssigned) {
                      // First operations employee gets hardware cost
                      grandTotal = hardwareTotal + descriptionTotal + totalCost;
                      hardwareCostAssigned = true;
                    } else {
                      // Other operations employees or when manager exists
                      grandTotal = descriptionTotal + totalCost;
                    }
                  } else {
                    // Other roles in hardware service
                    grandTotal = descriptionTotal + totalCost;
                  }
                } else {
                  // Non-hardware services: always description + cost
                  grandTotal = descriptionTotal + totalCost;
                }

                // HARD FIX: drop fake/duplicate rows that carry no data (prevents 0-hour duplicates)
                const isEmptyRow =
                  (Number(totalHours) || 0) === 0 &&
                  (Number(totalCost) || 0) === 0 &&
                  (Number(descriptionTotal) || 0) === 0 &&
                  (Number(hardwareTotal) || 0) === 0;
                if (isEmptyRow) return;

                const isInstallationService = String(item.TypeService || "")
                  .toLowerCase()
                  .includes("installation");
                const installationRates = isInstallationService
                  ? resolveInstallationPerdiemAndAverageCost(ProjectID)
                  : null;

                result.push({
                  servicewiseMargin,
                  ServiceQuotedValue,
                  isMarginupdated: item?.isMarginupdated ?? item?.isMarginUpdated ?? false,
                  quotationId: item?.quotationId,
                  projectName,
                  typeService: item.TypeService,
                  resourceType: item.ResourceType,
                  personId: normalizedPersonId,
                  ProjectID,
                  ProjectNotes,
                  personName: name,
                  roles: roles.map(role =>
                    role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
                  ),
                  // IMPORTANT: Total Cost Per Service must include efforts even for hardware services.
                  // Hardware rows may display 0 hourly rate in UI if desired, but calculations must retain effort costs.
                  totalHours,
                  hourlyRate: ratesReady ? effectiveHourlyRate : 0,
                  totalCost: ratesReady ? totalCost : 0,
                  descriptionTotal,
                  hardwareTotal,
                  grandTotal,
                  matchedDesignation: matchedQuotation?.designation || "Not matched",
                  isManager,
                  isOperations,
                  email,
                  ...(installationRates && (ratesLocked || isProjectQuoted)
                    ? {
                        installationPerdiemRate: installationRates.perdiem,
                        installationAveragecostRate: installationRates.averagecost,
                      }
                    : {}),
                });
              });
            }
          });
        }
      );

      return result;
    },
    [
      getTotalDescriptionValue,
      getTotalHardwareCostValue,
      resolveInstallationPerdiemAndAverageCost,
      ratesReady,
      ratesLocked,
      isProjectQuoted,
      scopedSalesHourlyRates,
      quotedproject,
      projects,
      selectedProject?.quotationId,
    ]
  );

  const groupByTypeService = (processedData) => {
    return processedData.reduce((acc, curr) => {
      if (!acc[curr.typeService]) {
        acc[curr.typeService] = [];
      }
      acc[curr.typeService].push(curr);
      return acc;
    }, {});
  };

  const processedData = useMemo(() => {
    return transformFilteredQuotation(filteredQuotation, employees, projects);
  }, [filteredQuotation, employees, transformFilteredQuotation, projects]);

  useEffect(() => {
    if (!ratesReady) return;
    console.log("Final Resources:", processedData);
  }, [ratesReady, processedData]);

  // Step-by-step: keep hourly rates at 0 for now.
  // Next step will enable `ratesReady` only after fresh SP rates are fetched/mapped.

  const groupedData = useMemo(() => {
    const raw = groupByTypeService(processedData);
    const isManagerRow = (row) => {
      const role = row?.roles?.[0]?.toLowerCase() || "";
      return role.includes("manager") || role.includes("project manager") || role.includes("pm");
    };

    const mergeRowCosts = (target, source) => ({
      ...target,
      totalHours: (Number(target.totalHours) || 0) + (Number(source.totalHours) || 0),
      totalCost: (Number(target.totalCost) || 0) + (Number(source.totalCost) || 0),
      descriptionTotal:
        (Number(target.descriptionTotal) || 0) + (Number(source.descriptionTotal) || 0),
      hardwareTotal: (Number(target.hardwareTotal) || 0) + (Number(source.hardwareTotal) || 0),
      grandTotal: (Number(target.grandTotal) || 0) + (Number(source.grandTotal) || 0),
    });

    // Fold duplicate same-person rows only; keep separate managers/employees with their own associated costs.
    const consolidateRoleBucket = (bucketRows) => {
      if (bucketRows.length <= 1) return bucketRows;

      const personKey = (row) =>
        (row?.email ? String(row.email).trim().toLowerCase() : "") ||
        (row?.personId ? String(row.personId).trim() : "") ||
        (row?.personName ? String(row.personName).trim().toLowerCase() : "");

      const byPerson = new Map();
      bucketRows.forEach((row) => {
        const key = personKey(row) || `__anon_${byPerson.size}`;
        if (!byPerson.has(key)) byPerson.set(key, []);
        byPerson.get(key).push(row);
      });

      const mergedRows = [];
      byPerson.forEach((personRows) => {
        if (personRows.length <= 1) {
          mergedRows.push(...personRows);
          return;
        }
        const withHours = personRows.filter((r) => (Number(r.totalHours) || 0) > 0);
        const withoutHours = personRows.filter((r) => (Number(r.totalHours) || 0) === 0);
        if (withHours.length === 1 && withoutHours.length > 0) {
          let merged = { ...withHours[0] };
          withoutHours.forEach((r) => {
            merged = mergeRowCosts(merged, r);
          });
          mergedRows.push(merged);
        } else {
          mergedRows.push(...personRows);
        }
      });
      return mergedRows;
    };

    const filtered = {};
    Object.entries(raw).forEach(([typeService, rows]) => {
      const managers = consolidateRoleBucket(rows.filter(isManagerRow));
      const employees = consolidateRoleBucket(rows.filter((r) => !isManagerRow(r)));
      filtered[typeService] = [...managers, ...employees];
    });
    return filtered;
  }, [processedData]);

  // Effective margin values for total: use same logic as margin init useEffect so first paint shows 100% (avoids 103.1% until state syncs)
  const effectiveMarginValuesForTotal = useMemo(() => {
    if (!groupedData || Object.keys(groupedData).length === 0) return {};
    const marginKeys = Object.entries(groupedData).map(([typeService, rows]) =>
      rows.length > 0 ? `${rows[0]?.ProjectID}-${typeService}` : null
    ).filter(Boolean);
    const allFromState = marginKeys.every((k) => marginValues[k] !== undefined);
    if (allFromState) return marginValues;
    const getServiceGrandTotal = (typeService, rows) => {
      const isHardware = typeService?.toLowerCase().includes("hardware");
      const hasManager = rows.some((r) => {
        const role = r.roles?.[0]?.toLowerCase();
        return role?.includes("manager") || role?.includes("project manager") || role?.includes("pm");
      });
      return rows.reduce((acc, row, idx) => {
        let rowGrandTotal = Number(row.grandTotal) || 0;
        if (isHardware && !hasManager && idx === 0) {
          rowGrandTotal = (Number(row.hardwareTotal) || 0) + (Number(row.descriptionTotal) || 0);
        }
        return acc + rowGrandTotal;
      }, 0);
    };
    const totalCost = Object.entries(groupedData).reduce(
      (sum, [typeService, rows]) => sum + getServiceGrandTotal(typeService, rows),
      0
    );
    const totalProjectQuoted = selectedProject?.QuotedValue || 0;
    const initial = {};
    const marginKeysOrder = [];
    let allCostCalculated = true;
    Object.entries(groupedData).forEach(([typeService, rows]) => {
      if (rows.length > 0) {
        const marginKey = `${rows[0]?.ProjectID}-${typeService}`;
        marginKeysOrder.push(marginKey);
        const isMarginupdated = rows[0]?.isMarginupdated === true || rows[0]?.isMarginUpdated === true;
        const dbMargin = rows[0]?.servicewiseMargin;
        const hasDbMargin = dbMargin != null && dbMargin !== "" && !isNaN(Number(dbMargin));
        if (isMarginupdated) {
          allCostCalculated = false;
          const savedMargin = hasDbMargin ? Number(dbMargin) : (totalProjectQuoted > 0 && rows[0]?.ServiceQuotedValue != null)
            ? (Number(rows[0].ServiceQuotedValue) / totalProjectQuoted) * 100
            : 0;
          initial[marginKey] = Number(savedMargin.toFixed(2));
        } else if (hasDbMargin && Number(dbMargin) !== 0) {
          allCostCalculated = false;
          initial[marginKey] = Number(dbMargin);
        } else {
          const serviceGrandTotal = getServiceGrandTotal(typeService, rows);
          const calculatedMargin = totalCost > 0 ? (serviceGrandTotal / totalCost) * 100 : 0;
          initial[marginKey] = Number(calculatedMargin.toFixed(2));
        }
      }
    });
    if (allCostCalculated && marginKeysOrder.length > 0) {
      return normalizeMarginsSumTo100(initial, marginKeysOrder);
    }
    return initial;
  }, [groupedData, marginValues, selectedProject?.QuotedValue]);

  // Normalized margins so sum is exactly 100.00 — used for Quoted Price formula: Quoted price per service = total quoted price × (Expected margin % / 100)
  const normalizedMarginsForCalc = useMemo(() => {
    if (!groupedData || Object.keys(groupedData).length === 0) return {};
    const rawByKey = {};
    let sumRaw = 0;
    Object.entries(groupedData).forEach(([typeService, rows]) => {
      if (rows.length > 0) {
        const marginKey = `${rows[0]?.ProjectID}-${typeService}`;
        const raw = effectiveMarginValuesForTotal[marginKey] !== undefined
          ? parseFloat(effectiveMarginValuesForTotal[marginKey]) || 0
          : rows[0]?.servicewiseMargin ?? 0;
        rawByKey[marginKey] = raw;
        sumRaw += raw;
      }
    });
    if (sumRaw <= 0) return rawByKey;
    const normalized = {};
    Object.keys(rawByKey).forEach((k) => {
      normalized[k] = (rawByKey[k] / sumRaw) * 100;
    });
    return normalized;
  }, [groupedData, effectiveMarginValuesForTotal]);

  // Per-service G&A for Itemized P&L: rounded per service, with remainder added to first service so sum = Math.round(totalGrossProfit * rate)
  const itemizedGAByService = useMemo(() => {
    if (!groupedData || Object.keys(groupedData).length === 0) return {};
    const entries = Object.entries(groupedData).sort(([a], [b]) => {
      if (a?.toLowerCase().includes("hardware")) return 1;
      if (b?.toLowerCase().includes("hardware")) return -1;
      return 0;
    });
    const gaRounded = [];
    let totalGP = 0;
    entries.forEach(([typeService, rows]) => {
      const isHardware = typeService?.toLowerCase().includes("hardware");
      const hasManager = rows.some((row) => {
        const role = row.roles[0]?.toLowerCase();
        return role?.includes("manager") || role?.includes("project manager") || role?.includes("pm");
      });
      const serviceGrandTotal = rows.reduce((acc, row, idx) => {
        let rowGrandTotal = Number(row.grandTotal) || 0;
        if (isHardware && !hasManager && idx === 0) {
          rowGrandTotal = (Number(row.hardwareTotal) || 0) + (Number(row.descriptionTotal) || 0);
        }
        return acc + rowGrandTotal;
      }, 0);
      const serviceKey = `${rows[0]?.ProjectID}-${typeService}`;
      const marginPct = normalizedMarginsForCalc[serviceKey] !== undefined
        ? normalizedMarginsForCalc[serviceKey]
        : (marginValues[serviceKey] !== undefined ? parseFloat(marginValues[serviceKey]) || 0 : rows[0]?.servicewiseMargin ?? 0);
      const quotedValuePerService = (selectedProject?.QuotedValue || 0) > 0
        ? (marginPct / 100) * (selectedProject?.QuotedValue || 0) : 0;
      const grossProfit = quotedValuePerService - serviceGrandTotal;
      totalGP += grossProfit;
      gaRounded.push({ typeService, ga: Math.round(grossProfit * (GandATax / 100)) });
    });
    const totalGA = Math.round(totalGP * (GandATax / 100));
    const sumRounded = gaRounded.reduce((s, x) => s + x.ga, 0);
    const diff = totalGA - sumRounded;
    const result = {};
    gaRounded.forEach((item, i) => {
      result[item.typeService] = item.ga + (i === 0 ? diff : 0);
    });
    return result;
  }, [groupedData, GandATax, selectedProject?.QuotedValue, marginValues, normalizedMarginsForCalc]);

  // Per-service Tax: rounded per service, remainder on first so sum = Math.round(totalGrossProfit * taxRate) — avoids 1₹ drift in totals
  const itemizedTaxByService = useMemo(() => {
    if (!groupedData || Object.keys(groupedData).length === 0) return {};
    const entries = Object.entries(groupedData).sort(([a], [b]) => {
      if (a?.toLowerCase().includes("hardware")) return 1;
      if (b?.toLowerCase().includes("hardware")) return -1;
      return 0;
    });
    const taxRounded = [];
    let totalGP = 0;
    entries.forEach(([typeService, rows]) => {
      const isHardware = typeService?.toLowerCase().includes("hardware");
      const hasManager = rows.some((row) => {
        const role = row.roles[0]?.toLowerCase();
        return role?.includes("manager") || role?.includes("project manager") || role?.includes("pm");
      });
      const serviceGrandTotal = rows.reduce((acc, row, idx) => {
        let rowGrandTotal = Number(row.grandTotal) || 0;
        if (isHardware && !hasManager && idx === 0) {
          rowGrandTotal = (Number(row.hardwareTotal) || 0) + (Number(row.descriptionTotal) || 0);
        }
        return acc + rowGrandTotal;
      }, 0);
      const serviceKey = `${rows[0]?.ProjectID}-${typeService}`;
      const marginPct = normalizedMarginsForCalc[serviceKey] !== undefined
        ? normalizedMarginsForCalc[serviceKey]
        : (marginValues[serviceKey] !== undefined ? parseFloat(marginValues[serviceKey]) || 0 : rows[0]?.servicewiseMargin ?? 0);
      const quotedValuePerService = (selectedProject?.QuotedValue || 0) > 0
        ? (marginPct / 100) * (selectedProject?.QuotedValue || 0) : 0;
      const grossProfit = quotedValuePerService - serviceGrandTotal;
      totalGP += grossProfit;
      taxRounded.push({ typeService, tax: Math.round(grossProfit * (taxValue / 100)) });
    });
    const totalTax = Math.round(totalGP * (taxValue / 100));
    const sumRounded = taxRounded.reduce((s, x) => s + x.tax, 0);
    const diff = totalTax - sumRounded;
    const result = {};
    taxRounded.forEach((item, i) => {
      result[item.typeService] = item.tax + (i === 0 ? diff : 0);
    });
    return result;
  }, [groupedData, taxValue, selectedProject?.QuotedValue, marginValues, normalizedMarginsForCalc]);

  // Per-service Net Revenue: grossProfit - itemized G&A - itemized Tax, then rounded with remainder on first so row sum = total (fixes 1₹ difference)
  const itemizedNetRevenueByService = useMemo(() => {
    if (!groupedData || Object.keys(groupedData).length === 0) return {};
    const entries = Object.entries(groupedData).sort(([a], [b]) => {
      if (a?.toLowerCase().includes("hardware")) return 1;
      if (b?.toLowerCase().includes("hardware")) return -1;
      return 0;
    });
    const netRevRounded = [];
    let totalNetRevRaw = 0;
    entries.forEach(([typeService, rows]) => {
      const isHardware = typeService?.toLowerCase().includes("hardware");
      const hasManager = rows.some((row) => {
        const role = row.roles[0]?.toLowerCase();
        return role?.includes("manager") || role?.includes("project manager") || role?.includes("pm");
      });
      const serviceGrandTotal = rows.reduce((acc, row, idx) => {
        let rowGrandTotal = Number(row.grandTotal) || 0;
        if (isHardware && !hasManager && idx === 0) {
          rowGrandTotal = (Number(row.hardwareTotal) || 0) + (Number(row.descriptionTotal) || 0);
        }
        return acc + rowGrandTotal;
      }, 0);
      const serviceKey = `${rows[0]?.ProjectID}-${typeService}`;
      const marginPct = normalizedMarginsForCalc[serviceKey] !== undefined
        ? normalizedMarginsForCalc[serviceKey]
        : (marginValues[serviceKey] !== undefined ? parseFloat(marginValues[serviceKey]) || 0 : rows[0]?.servicewiseMargin ?? 0);
      const quotedValuePerService = (selectedProject?.QuotedValue || 0) > 0
        ? (marginPct / 100) * (selectedProject?.QuotedValue || 0) : 0;
      const grossProfit = quotedValuePerService - serviceGrandTotal;
      const ga = itemizedGAByService[typeService] ?? Math.round(grossProfit * (GandATax / 100));
      const tax = itemizedTaxByService[typeService] ?? Math.round(grossProfit * (taxValue / 100));
      const netRev = grossProfit - ga - tax;
      totalNetRevRaw += netRev;
      netRevRounded.push({ typeService, netRev: Math.round(netRev) });
    });
    const totalNetRevRounded = Math.round(totalNetRevRaw);
    const sumRounded = netRevRounded.reduce((s, x) => s + x.netRev, 0);
    const diff = totalNetRevRounded - sumRounded;
    const result = {};
    netRevRounded.forEach((item, i) => {
      result[item.typeService] = item.netRev + (i === 0 ? diff : 0);
    });
    return result;
  }, [groupedData, itemizedGAByService, itemizedTaxByService, GandATax, taxValue, selectedProject?.QuotedValue, marginValues, normalizedMarginsForCalc]);

  useEffect(() => {
    const totalDescription = Object.values(groupedData).reduce((total, serviceArray) => {
      // Sum descriptionTotal for each service type
      const serviceTotal = serviceArray.reduce((sum, item) => {
        // Make sure descriptionTotal exists and is a number, default to 0 if not
        return sum + (item.descriptionTotal || 0);
      }, 0);

      return total + serviceTotal;
    }, 0);


    setMatchedDescription(totalDescription)
  }, [groupedData]);

  // CRITICAL: When switching leads, clear derived totals so we never display the previous lead's totals
  // in the Itemized P&L panel (this was causing the same "12,606" to appear for unrelated leads).
  useEffect(() => {
    setTotalServiceGrandTotal(0);
    lastEmittedTotalCostPerServiceRef.current = null;
  }, [selectedProject?.quotationId, selectedProject?.ProjectName]);


  useEffect(() => {
    if (groupedData && Object.keys(groupedData).length > 0) {
      let sum = 0;
      Object.entries(groupedData).forEach(([typeService, rows]) => {
        // Total Cost Per Service must match the Itemized column (grandTotal already applies the correct hardware assignment + efforts + associated costs).
        const serviceGrandTotal = rows.reduce((acc, row) => acc + (Number(row.grandTotal) || 0), 0);

        sum += serviceGrandTotal;
      });
      setTotalServiceGrandTotal(sum);
    } else {
      setTotalServiceGrandTotal(0);
    }
  }, [groupedData]);

  // Expose Itemized P&L "Total Cost Per Service" total to parent (for Quoted Expenses when quotation not created)
  useEffect(() => {
    if (typeof onTotalCostPerServiceTotalChange !== "function") return;
    if (isProjectQuoted) return; // locked quoted projects should not be overridden
    const rounded = Math.round(Number(totalServiceGrandTotal) || 0);
    // Avoid emitting a meaningless 0 while rates are still loading.
    if (!ratesReady && rounded === 0) return;
    if (lastEmittedTotalCostPerServiceRef.current === rounded) return;
    lastEmittedTotalCostPerServiceRef.current = rounded;
    const projectKey =
      selectedProject?.quotationId != null && String(selectedProject.quotationId).trim() !== ""
        ? String(selectedProject.quotationId).trim()
        : String(selectedProject?.ProjectName ?? "").trim();
    onTotalCostPerServiceTotalChange(projectKey, rounded);
  }, [
    onTotalCostPerServiceTotalChange,
    totalServiceGrandTotal,
    ratesReady,
    isProjectQuoted,
    selectedProject?.ProjectName,
    selectedProject?.quotationId,
  ]);

  useEffect(() => {
    if (selected && tableContainerRef.current) {
      // Scroll to the right end of the container
      const scrollToPosition = tableContainerRef.current.scrollWidth;
      tableContainerRef.current.scrollTo({
        left: scrollToPosition,
        behavior: "smooth",
      });
    }
  }, [selected]);

  useEffect(() => {
    if (groupedData && Object.keys(groupedData).length > 0) {
      let grossProfitSum = 0;
      let totalQuotedValueSum = 0;

      Object.entries(groupedData).forEach(([typeService, rows]) => {
        const isHardware = typeService?.toLowerCase().includes("hardware");
        const hasManager = rows.some((row) => {
          const role = row.roles[0]?.toLowerCase();
          return (
            role?.includes("manager") ||
            role?.includes("project manager") ||
            role?.includes("pm")
          );
        });
        const serviceGrandTotal = rows.reduce((acc, row, idx) => {
          let rowGrandTotal = Number(row.grandTotal) || 0;

          // If hardware, no manager, and this is the first employee, include hardwareTotal
          if (isHardware && !hasManager && idx === 0) {
            rowGrandTotal =
              (Number(row.hardwareTotal) || 0) +
              (Number(row.descriptionTotal) || 0);
          }

          return acc + rowGrandTotal;
        }, 0);
        const serviceKey = `${rows[0]?.ProjectID}-${typeService}`;
        const marginPct = normalizedMarginsForCalc[serviceKey] !== undefined
          ? normalizedMarginsForCalc[serviceKey]
          : (marginValues[serviceKey] !== undefined ? parseFloat(marginValues[serviceKey]) || 0 : rows[0]?.servicewiseMargin ?? 0);
        const quotedValuePerService =
          (selectedProject?.QuotedValue || 0) > 0 ? (marginPct / 100) * (selectedProject?.QuotedValue || 0) : 0;
        totalQuotedValueSum += quotedValuePerService;

        grossProfitSum += quotedValuePerService - serviceGrandTotal;
      });
      // Use itemized rounded totals so column total exactly matches sum of row values (fixes 1₹ drift from Expected margin % decimals)
      const totalGA = Math.round(grossProfitSum * (GandATax / 100));
      const totalTaxRounded = Object.values(itemizedTaxByService).reduce((s, v) => s + v, 0);
      const totalNetRevenueRounded = Object.values(itemizedNetRevenueByService).reduce((s, v) => s + v, 0);
      const totalQuotedPrice = totalQuotedValueSum > 0 ? totalQuotedValueSum : (selectedProject?.QuotedValue || 0);
      const netRevenuePct =
        totalQuotedPrice > 0 && totalNetRevenueRounded !== 0
          ? (totalNetRevenueRounded / totalQuotedPrice) * 100
          : totalQuotedPrice > 0
            ? ((grossProfitSum - totalGA - totalTaxRounded) / totalQuotedPrice) * 100
            : 0;
      setTotalGrossProfit(grossProfitSum);
      setTotalGAExpenses(totalGA);
      setTotalTax(totalTaxRounded);
      setTotalNetRevenue(totalNetRevenueRounded);
      setNetRevenuePercentage(netRevenuePct);
    }
  }, [groupedData, selectedProject?.QuotedValue, GandATax, taxValue, marginValues, normalizedMarginsForCalc, itemizedTaxByService, itemizedNetRevenueByService]);

  // Push Itemized P&L totals to parent so main P&L table uses same values (source of truth)
  useEffect(() => {
    if (
      typeof onItemizedTotalsChange === "function" &&
      selectedProject?.ProjectName &&
      groupedData &&
      Object.keys(groupedData).length > 0
    ) {
      const projectKey =
        selectedProject?.quotationId != null && String(selectedProject.quotationId).trim() !== ""
          ? String(selectedProject.quotationId).trim()
          : selectedProject?.ProjectName ?? "";
      onItemizedTotalsChange(projectKey, {
        totalCostPerService: Math.round(Number(totalServiceGrandTotal) || 0),
        grossProfit: totalGrossProfit,
        gaExpenses: totalGAExpenses,
        tax: totalTax,
        netRevenue: totalNetRevenue,
        netRevenuePct: netRevenuePercentage,
      });
    }
  }, [
    onItemizedTotalsChange,
    selectedProject,
    totalServiceGrandTotal,
    totalGrossProfit,
    totalGAExpenses,
    totalTax,
    totalNetRevenue,
    netRevenuePercentage,
    groupedData,
  ]);

  useEffect(() => {
    if (groupedData && Object.keys(groupedData).length > 0) {
      let hoursSum = 0;
      Object.values(groupedData).forEach((rows) => {
        rows.forEach((row) => {
          // Only add hours for non-Hardware services
          if (row.typeService !== "Hardware") {
            hoursSum += row.totalHours || 0;
          }
        });
      });

      setTotalHoursSum(hoursSum);
    }
  }, [groupedData]);

  // Total Efforts total must use same source as row data (groupedData) so it matches
  // Formula: Total Efforts = Sum of (Hours * Hourly Rate) per row; row.totalCost = totalHours * hourlyRate
  useEffect(() => {
    if (!groupedData || Object.keys(groupedData).length === 0) {
      setTotalCostSum(0);
      return;
    }
    if (!ratesReady) {
      setTotalCostSum(0);
      return;
    }

    const sumFromGroupedData = Object.values(groupedData).reduce(
      (sum, rows) =>
        sum + rows.reduce((rowSum, row) => rowSum + (Number(row.totalCost) || 0), 0),
      0
    );
    setTotalCostSum(sumFromGroupedData);

    // Debug log when project is quoted (isProjectQuoted: true) to verify Total Efforts formula
    if (isProjectQuoted) {
      const byRow = [];
      Object.entries(groupedData).forEach(([typeService, rows]) => {
        rows.forEach((row) => {
          const hours = Number(row.totalHours) || 0;
          const rate = Number(row.hourlyRate) || 0;
          const totalCost = Number(row.totalCost) || 0;
          byRow.push({
            typeService,
            personName: row.personName,
            hours,
            hourlyRate: rate,
            totalCost,
            formula: `${hours} * ${rate} = ${hours * rate}`,
            match: Math.abs((hours * rate) - totalCost) < 0.01 ? "ok" : "mismatch",
          });
        });
      });
      console.log("[Itemized P&L] Total Efforts formula: each row = Hours * Hourly Rate; total = sum of row.totalCost", {
        formula: "Total Efforts total = Sum of (Hours × Hourly Rate) per row",
        totalFromGroupedData: sumFromGroupedData,
        rowBreakdown: byRow,
      });
    }
  }, [groupedData, isProjectQuoted, ratesReady]);

  const saveNoteToAPI = useCallback(
    async (typeService, projectID, noteText) => {
      try {
        dispatch(
          replaceProjectFinanceNotes({
            id: projectID,
            ProjectFinanceNotes: noteText,
          })
        );
      } catch (error) {
        console.error("Error saving note:", error);
      } finally {
        enqueueSnackbar("Notes Added Successfully", { variant: "success" });
      }
    },
    [dispatch, enqueueSnackbar]
  );
  const handleNoteChange = useCallback(
    (typeService, projectID, noteText) => {
      // Create a unique key for this project-service combination
      const noteKey = `${projectID}-${typeService}`;

      // Update local state immediately for responsive UI
      setNotes((prev) => ({
        ...prev,
        [noteKey]: noteText,
      }));

      // Clear existing timeout for this noteKey
      if (saveTimeout[noteKey]) {
        clearTimeout(saveTimeout[noteKey]);
      }
      // Set new timeout for API call (debounce of 1 second)
      const newTimeout = setTimeout(() => {
        saveNoteToAPI(typeService, projectID, noteText);
      }, 2000);

      // Update timeout reference
      setSaveTimeout((prev) => ({
        ...prev,
        [noteKey]: newTimeout,
      }));
    },
    [saveNoteToAPI, saveTimeout]
  );
  const recalculateTotals = useCallback(() => {
    if (groupedData && Object.keys(groupedData).length > 0) {
      let grossProfitSum = 0;
      Object.entries(groupedData).forEach(([typeService, rows]) => {
        const isHardware = typeService?.toLowerCase().includes("hardware");
        const hasManager = rows.some((row) => {
          const role = row.roles[0]?.toLowerCase();
          return (
            role?.includes("manager") ||
            role?.includes("project manager") ||
            role?.includes("pm")
          );
        });
        const serviceGrandTotal = rows.reduce((acc, row, idx) => {
          let rowGrandTotal = Number(row.grandTotal) || 0;
          if (isHardware && !hasManager && idx === 0) {
            rowGrandTotal =
              (Number(row.hardwareTotal) || 0) +
              (Number(row.descriptionTotal) || 0);
          }
          return acc + rowGrandTotal;
        }, 0);
        const serviceKey = `${rows[0]?.ProjectID}-${typeService}`;
        const marginPct = normalizedMarginsForCalc[serviceKey] !== undefined
          ? normalizedMarginsForCalc[serviceKey]
          : (marginValues[serviceKey] !== undefined ? parseFloat(marginValues[serviceKey]) || 0 : rows[0]?.servicewiseMargin ?? 0);
        const totalProjectQuoted = Number(selectedProject?.QuotedValue) || 0;
        const quotedValue = totalProjectQuoted > 0 ? (marginPct / 100) * totalProjectQuoted : 0;
        grossProfitSum += quotedValue - serviceGrandTotal;
      });
      const totalQuotedValue = Number(selectedProject?.QuotedValue) || 0;
      const totalGA = Math.round(grossProfitSum * (GandATax / 100));
      const totalTaxRounded = Object.values(itemizedTaxByService).reduce((s, v) => s + v, 0);
      const totalNetRevenueRounded = Object.values(itemizedNetRevenueByService).reduce((s, v) => s + v, 0);
      const netRevenuePct =
        totalQuotedValue > 0 ? (totalNetRevenueRounded / totalQuotedValue) * 100 : 0;
      setTotalGrossProfit(grossProfitSum);
      setTotalGAExpenses(totalGA);
      setTotalTax(totalTaxRounded);
      setTotalNetRevenue(totalNetRevenueRounded);
      setNetRevenuePercentage(netRevenuePct);
    }
  }, [
    groupedData,
    selectedProject?.QuotedValue,
    GandATax,
    marginValues,
    normalizedMarginsForCalc,
    itemizedTaxByService,
    itemizedNetRevenueByService,
  ]);


  const [isUserEditing,] = useState({});


  // Update the initialization useEffect to use non-user action
  useEffect(() => {
    if (processedData.length > 0) {
      const initialQuotedValues = {};

      Object.entries(groupedData).forEach(([typeService, rows]) => {
        if (rows.length > 0) {
          const valueKey = `${rows[0]?.ProjectID}-${typeService}`;
          // Only initialize if user is not currently editing this field
          if (!isUserEditing[valueKey]) {
            initialQuotedValues[valueKey] = rows[0]?.ServiceQuotedValue || "";
          }
        }
      });

      setQuotedValues((prev) => ({
        ...prev,
        ...initialQuotedValues,
      }));
    }
  }, [processedData, groupedData, isUserEditing]);

  useEffect(() => {
    setSoftwarePersons(groupedData.Software || []);
    setCloudPersons(groupedData.Cloud || []);
    setHardwarePersons(groupedData.Hardware || []);
    setinstallationPersons(groupedData.Installation || []);
  }, [
    groupedData,
    setSoftwarePersons,
    setCloudPersons,
    setHardwarePersons,
    setinstallationPersons,
  ]);

  const calculateTotalServiceQuotedValue = useCallback(() => {
    const totalProjectQuoted = Number(selectedProject?.QuotedValue) || 0;
    if (!totalProjectQuoted) return 0;
    return Object.values(groupedData).reduce((total, rows) => {
      const serviceKey = `${rows[0]?.ProjectID}-${rows[0]?.typeService}`;
      const marginPct = normalizedMarginsForCalc[serviceKey] !== undefined
        ? normalizedMarginsForCalc[serviceKey]
        : (marginValues[serviceKey] !== undefined ? parseFloat(marginValues[serviceKey]) || 0 : rows[0]?.servicewiseMargin ?? 0);
      return total + (marginPct / 100) * totalProjectQuoted;
    }, 0);
  }, [groupedData, marginValues, normalizedMarginsForCalc, selectedProject?.QuotedValue]);

  const updateMarginAPI = useCallback(
    async (row, serviceGrandTotal, typeService, projectID, marginValue, options = {}) => {
      const { skipRefresh = false, skipLoader = false } = options;

      const totalServiceQuotedValue = calculateTotalServiceQuotedValue();

      if (!skipLoader) SetisLoading(true);
      try {
        const salesmargin = Number(
          marginValue.toString().replace(/[^0-9.-]+/g, "")
        );
        const quotationId = row?.quotationId ?? selectedProject?.quotationId ?? null;
        const ProjectName = row?.projectName ?? selectedProject?.ProjectName ?? null;
        const TypeService = typeService;
        await dispatch(
          updateSalesMargin({
            quotationId,
            ProjectName,
            TypeService,
            salesmargin,
            QuotedValue: totalServiceQuotedValue,
          })
        ).unwrap();

        if (!skipRefresh) {
          enqueueSnackbar("Margin Updated Successfully", { variant: "success" });
        }
      } catch (error) {
        console.error("Error updating margin:", error);
        enqueueSnackbar("Error updating margin", { variant: "error" });
      } finally {
        recalculateTotals();
        if (!skipRefresh) {
          // Use quotationId when present (same as P&L table row identification)
          const refreshKey = row?.quotationId != null && String(row.quotationId).trim() !== ""
            ? String(row.quotationId).trim()
            : (row?.projectName ?? row?.ProjectName);
          handlerefresh(refreshKey);
          setMarginValues({});
        }
        if (!skipLoader) SetisLoading(false);
        if (marginValidationTimer) {
          clearTimeout(marginValidationTimer);
        }
      }
    },
    [dispatch, handlerefresh, marginValidationTimer, recalculateTotals, enqueueSnackbar, calculateTotalServiceQuotedValue, selectedProject]
  );


  useEffect(() => {
    // Prevent re-entry: only one batch at a time; avoids continuous API calls and loader stuck on
    if (isBatchUpdatingMarginsRef.current) return;
    // Do not update margins via API when project is quoted; only show saved margin from DB
    if (isProjectQuoted) return;

    // Calculate total margin percentage
    const calculatedTotalMargin = Object.values(groupedData).reduce((total, rows) => {
      const marginKey = `${rows[0]?.ProjectID}-${rows[0]?.typeService}`;
      const marginValue = marginValues[marginKey] !== undefined
        ? parseFloat(marginValues[marginKey]) || 0
        : rows[0]?.servicewiseMargin || 0;
      return total + marginValue;
    }, 0);
    const calculatedTotalMargin2 = Number(calculatedTotalMargin.toFixed(2));

    // Don't proceed if no margin values or if user hasn't edited anything
    if (marginValues == null || Object.keys(marginValues).length === 0 || editedMarginKeys.size === 0) {
      return;
    }

    // Only proceed when the user-entered total is exactly 100.00 (2dp) — don't auto-round 99.98/99.99 to 100.
    if (calculatedTotalMargin2 === 100) {
      const updatePromises = [];
      Object.entries(groupedData).forEach(([typeService, rows]) => {
        if (rows.length > 0) {
          const marginKey = `${rows[0]?.ProjectID}-${typeService}`;

          // At 100% total, persist every service margin (not only last-edited row).
          {
            const serviceGrandTotal = rows.reduce((acc, row, idx) => {
              const isHardware = typeService?.toLowerCase().includes("hardware");
              const hasManager = rows.some((row) => {
                const role = row.roles[0]?.toLowerCase();
                return (
                  role?.includes("manager") ||
                  role?.includes("project manager") ||
                  role?.includes("pm")
                );
              });

              let rowGrandTotal = Number(row.grandTotal) || 0;
              if (isHardware && !hasManager && idx === 0) {
                rowGrandTotal =
                  (Number(row.hardwareTotal) || 0) +
                  (Number(row.descriptionTotal) || 0);
              }
              return acc + rowGrandTotal;
            }, 0);

            const marginValue = marginValues[marginKey] !== undefined
              ? parseFloat(marginValues[marginKey]) || 0
              : rows[0]?.servicewiseMargin || 0;

            updatePromises.push(
              updateMarginAPI(rows[0], serviceGrandTotal, typeService, rows[0]?.ProjectID, marginValue, {
                skipRefresh: true,
                skipLoader: true,
              })
            );
          }
        }
      });

      if (updatePromises.length === 0) return;

      // Single batch: one loader, all API calls, then one refresh and one snackbar
      isBatchUpdatingMarginsRef.current = true;
      SetisLoading(true);
      Promise.all(updatePromises)
        .then(() => {
          const snap = { ...(marginValuesRef.current || {}) };
          savedMarginsAfterBatchRef.current = snap;
          marginsLockedAfterSaveRef.current = true;
          setMarginValues(snap);
          enqueueSnackbar("Margins Updated Successfully", { variant: "success" });
          setEditedMarginKeys(new Set());
          if (preserveMarginsOnRefresh) {
            if (typeof onWizardMarginsSaved === "function") {
              onWizardMarginsSaved();
            }
          } else {
            const refreshKey =
              selectedProject?.quotationId != null &&
              String(selectedProject.quotationId).trim() !== ""
                ? String(selectedProject.quotationId).trim()
                : selectedProject?.ProjectName;
            handlerefresh(refreshKey);
          }
        })
        .catch(() => {
          // Error snackbars already shown by updateMarginAPI
        })
        .finally(() => {
          SetisLoading(false);
          isBatchUpdatingMarginsRef.current = false;
        });
    }
  }, [marginValues, groupedData, updateMarginAPI, editedMarginKeys, selectedProject, enqueueSnackbar, handlerefresh, isProjectQuoted, preserveMarginsOnRefresh, onWizardMarginsSaved]);

  const totalMarginPercentage = Object.values(groupedData).reduce((total, rows) => {
    const marginKey = `${rows[0]?.ProjectID}-${rows[0]?.typeService}`;
    const marginValue = effectiveMarginValuesForTotal[marginKey] !== undefined
      ? parseFloat(effectiveMarginValuesForTotal[marginKey]) || 0
      : rows[0]?.servicewiseMargin || 0;
    return total + marginValue;
  }, 0);

  // Keep a stable numeric value at 2dp for validation/display (prevents float drift and avoids snapping 99.98 → 100).
  const totalMarginPercentage2 = Number(totalMarginPercentage.toFixed(2));
  totalMarginPercentageRef.current = totalMarginPercentage2;
  const displayTotalMargin = totalMarginPercentage2.toFixed(2);


  const validateMarginDebounced = useCallback(() => {
    // Clear any existing timer
    if (marginValidationTimer) {
      clearTimeout(marginValidationTimer);
    }

    // Set a new timer that triggers after 2 seconds of no typing
    const timer = setTimeout(() => {
      // Don't show alert during API loading - avoids false positive when total is 100 but data is settling
      if (isLoading) return;

      // Use ref to avoid stale closure when total updates (e.g. after API refresh) before timer fires
      const currentTotal = Number(totalMarginPercentageRef.current ?? 0);
      const isLessThanHundred = currentTotal < 100;

      // Check if we should show alert (after 5 seconds since last alert)
      const now = Date.now();
      const shouldShowAlert = isLessThanHundred &&
        (!lastMarginAlertTime || (now - lastMarginAlertTime) > 5000);

      if (shouldShowAlert) {
        enqueueSnackbar("Total margin must be 100%", {
          variant: "error",
          autoHideDuration: 3000,
          preventDuplicate: true
        });
        setLastMarginAlertTime(now);
      }
    }, 2000); // 2 second delay after user stops typing

    setMarginValidationTimer(timer);
  }, [enqueueSnackbar, lastMarginAlertTime, marginValidationTimer, isLoading]);

  const handleMarginChange = useCallback(
    (typeService, row, projectID, rawValue, serviceGrandTotal, e) => {
      const marginKey = `${projectID}-${typeService}`;
      marginsLockedAfterSaveRef.current = false;
      setEditedMarginKeys(prev => new Set(prev).add(marginKey));

      // Update local state immediately for responsive UI
      setMarginValues((prev) => ({
        ...prev,
        [marginKey]: rawValue,
      }));

      validateMarginDebounced();
      // Don't show alert on every change, only set flag
      // setShowMarginAlert(true) - remove this line
    },
    [validateMarginDebounced]
  );

  useEffect(() => {
    const projectIdentifier = String(
      selectedProject?.quotationId ??
        selectedProject?.id ??
        selectedProject?.ProjectName ??
        selectedProject ??
        ""
    );
    if (marginLockProjectRef.current !== projectIdentifier) {
      marginLockProjectRef.current = projectIdentifier;
      marginsLockedAfterSaveRef.current = false;
      savedMarginsAfterBatchRef.current = null;
      lastMarginInitKeyRef.current = "";
      lastMarginRecalcCostSigRef.current = "";
    }
  }, [
    selectedProject?.quotationId,
    selectedProject?.id,
    selectedProject?.ProjectName,
    selectedProject,
  ]);




  // When salesmargin present in DB: show DB value. When null/0: calculate from cost proportion and show (parent will persist via updatemargin).
  useEffect(() => {
    if (processedData.length === 0 || !groupedData || Object.keys(groupedData).length === 0) return;
    const projectIdentifier = selectedProject?.quotationId ?? selectedProject?.id ?? selectedProject?.ProjectName ?? "";
    const dataKey = preserveMarginsOnRefresh
      ? String(projectIdentifier)
      : `${projectIdentifier}_${Object.keys(groupedData).sort().join(",")}`;
    if (marginsLockedAfterSaveRef.current) return;
    if (
      preserveMarginsOnRefresh &&
      lastMarginInitKeyRef.current === dataKey &&
      Object.keys(marginValuesRef.current || {}).length > 0
    ) {
      return;
    }
    if (lastMarginInitKeyRef.current === dataKey) return;
    lastMarginInitKeyRef.current = dataKey;

    const getServiceGrandTotal = (typeService, rows) => {
      const isHardware = typeService?.toLowerCase().includes("hardware");
      const hasManager = rows.some((r) => {
        const role = r.roles?.[0]?.toLowerCase();
        return role?.includes("manager") || role?.includes("project manager") || role?.includes("pm");
      });
      return rows.reduce((acc, row, idx) => {
        let rowGrandTotal = Number(row.grandTotal) || 0;
        if (isHardware && !hasManager && idx === 0) {
          rowGrandTotal = (Number(row.hardwareTotal) || 0) + (Number(row.descriptionTotal) || 0);
        }
        return acc + rowGrandTotal;
      }, 0);
    };

    const totalCost = Object.entries(groupedData).reduce(
      (sum, [typeService, rows]) => sum + getServiceGrandTotal(typeService, rows),
      0
    );

    const totalProjectQuoted = selectedProject?.QuotedValue || 0;
    const initialMarginValues = {};
    const marginKeysOrder = [];
    let allCostCalculated = true;
    Object.entries(groupedData).forEach(([typeService, rows]) => {
      if (rows.length > 0) {
        const marginKey = `${rows[0]?.ProjectID}-${typeService}`;
        marginKeysOrder.push(marginKey);
        const isMarginupdated = rows[0]?.isMarginupdated === true || rows[0]?.isMarginUpdated === true;
        const dbMargin = rows[0]?.servicewiseMargin;
        const hasDbMargin = dbMargin != null && dbMargin !== "" && !isNaN(Number(dbMargin));
        // When project is quoted (isProjectQuoted: true): only show saved margin from DB, do not recalculate
        if (isProjectQuoted) {
          allCostCalculated = false;
          const savedMargin = hasDbMargin ? Number(dbMargin) : (isMarginupdated && totalProjectQuoted > 0 && rows[0]?.ServiceQuotedValue != null)
            ? (Number(rows[0].ServiceQuotedValue) / totalProjectQuoted) * 100
            : 0;
          initialMarginValues[marginKey] = Number(Number(savedMargin).toFixed(2));
        } else if (isMarginupdated) {
          allCostCalculated = false;
          const savedMargin = hasDbMargin ? Number(dbMargin) : (totalProjectQuoted > 0 && rows[0]?.ServiceQuotedValue != null)
            ? (Number(rows[0].ServiceQuotedValue) / totalProjectQuoted) * 100
            : 0;
          initialMarginValues[marginKey] = Number(savedMargin.toFixed(2));
        } else if (hasDbMargin && Number(dbMargin) !== 0) {
          allCostCalculated = false;
          initialMarginValues[marginKey] = Number(dbMargin);
        } else {
          const serviceGrandTotal = getServiceGrandTotal(typeService, rows);
          const calculatedMargin = totalCost > 0 ? (serviceGrandTotal / totalCost) * 100 : 0;
          initialMarginValues[marginKey] = Number(calculatedMargin.toFixed(2));
        }
      }
    });
    const marginsToSet =
      allCostCalculated && marginKeysOrder.length > 0 && !isProjectQuoted
        ? normalizeMarginsSumTo100(initialMarginValues, marginKeysOrder)
        : initialMarginValues;
    setMarginValues(marginsToSet);
  }, [processedData, groupedData, selectedProject?.quotationId, selectedProject?.id, selectedProject?.ProjectName, selectedProject?.QuotedValue, isProjectQuoted, preserveMarginsOnRefresh]);

  /**
   * Recalculate margins when costs change (hourly rates / total cost per service),
   * while preserving user-edited margins and keeping the total at 100.
   */
  useEffect(() => {
    if (isProjectQuoted) return; // locked quoted projects should not be overridden
    if (!ratesReady) return; // avoid recalculating while rates are intentionally 0 during fetch/reset
    if (!groupedData || Object.keys(groupedData).length === 0) return;

    const getServiceGrandTotal = (typeService, rows) => {
      const isHardware = typeService?.toLowerCase().includes("hardware");
      const hasManager = rows.some((r) => {
        const role = r.roles?.[0]?.toLowerCase();
        return (
          role?.includes("manager") ||
          role?.includes("project manager") ||
          role?.includes("pm")
        );
      });
      return rows.reduce((acc, row, idx) => {
        let rowGrandTotal = Number(row.grandTotal) || 0;
        if (isHardware && !hasManager && idx === 0) {
          rowGrandTotal =
            (Number(row.hardwareTotal) || 0) + (Number(row.descriptionTotal) || 0);
        }
        return acc + rowGrandTotal;
      }, 0);
    };

    const serviceEntries = Object.entries(groupedData)
      .map(([typeService, rows]) => {
        if (!rows || rows.length === 0 || !rows[0]) return null;
        const key = `${rows[0]?.ProjectID}-${typeService}`;
        const cost = getServiceGrandTotal(typeService, rows);
        const dbMargin = rows[0]?.servicewiseMargin;
        const hasDbMargin =
          dbMargin != null && dbMargin !== "" && !isNaN(Number(dbMargin));
        const isMarginupdated =
          rows[0]?.isMarginupdated === true || rows[0]?.isMarginUpdated === true;
        return { key, cost, dbMargin, hasDbMargin, isMarginupdated };
      })
      .filter(Boolean);

    if (serviceEntries.length === 0) return;

    // Only recalc when underlying costs change (not on margin typing).
    const costSig = serviceEntries
      .map((e) => `${e.key}:${Number(e.cost || 0).toFixed(4)}`)
      .sort()
      .join("|");
    if (marginsLockedAfterSaveRef.current) {
      if (savedMarginsAfterBatchRef.current) {
        setMarginValues({ ...savedMarginsAfterBatchRef.current });
        savedMarginsAfterBatchRef.current = null;
      }
      lastMarginRecalcCostSigRef.current = costSig;
      return;
    }

    // Do not auto-redistribute saved margins when user is not actively editing.
    const allMarginsSavedInDb =
      serviceEntries.length > 0 &&
      serviceEntries.every((e) => e.isMarginupdated);
    if (
      editedMarginKeys.size === 0 &&
      (preserveMarginsOnRefresh || allMarginsSavedInDb)
    ) {
      lastMarginRecalcCostSigRef.current = costSig;
      return;
    }

    if (lastMarginRecalcCostSigRef.current === costSig) return;

    if (preserveMarginsOnRefresh && savedMarginsAfterBatchRef.current) {
      const snap = savedMarginsAfterBatchRef.current;
      savedMarginsAfterBatchRef.current = null;
      setMarginValues(snap);
      lastMarginRecalcCostSigRef.current = costSig;
      return;
    }

    lastMarginRecalcCostSigRef.current = costSig;

    const totalCost = serviceEntries.reduce((s, e) => s + (Number(e.cost) || 0), 0);
    const editedSet = editedMarginKeys || new Set();

    const clamp = (n) => {
      const x = Number(n);
      if (!Number.isFinite(x)) return 0;
      return Math.max(0, Math.min(100, x));
    };

    const rawByKey = {};
    const editedKeys = [];
    const nonEditedKeys = [];

    const latestMargins = marginValuesRef.current || {};

    serviceEntries.forEach((e) => {
      const key = e.key;
      const isEdited = editedSet.has(key);

      if (isEdited) {
        editedKeys.push(key);
        const v = parseFloat(latestMargins?.[key]);
        rawByKey[key] = Number.isFinite(v) ? v : 0;
        return;
      }

      nonEditedKeys.push(key);

      // Preserve existing distribution logic:
      // - `isMarginupdated` means margin was set/distributed earlier → keep unless user edits.
      // - Otherwise, keep a non-zero DB margin as baseline.
      if (e.isMarginupdated) {
        const v = e.hasDbMargin ? Number(e.dbMargin) : 0;
        rawByKey[key] = Number.isFinite(v) ? v : 0;
        return;
      }
      if (e.hasDbMargin && Number(e.dbMargin) !== 0) {
        const v = Number(e.dbMargin);
        rawByKey[key] = Number.isFinite(v) ? v : 0;
        return;
      }

      rawByKey[key] = totalCost > 0 ? (Number(e.cost) / totalCost) * 100 : 0;
    });

    const fixedSum = editedKeys.reduce((s, k) => s + clamp(rawByKey[k]), 0);
    const remaining = Math.max(0, 100 - fixedSum);

    const nextByKey = {};

    if (nonEditedKeys.length === 0) {
      const sumAll = serviceEntries.reduce((s, e) => s + clamp(rawByKey[e.key]), 0);
      if (sumAll > 0) {
        serviceEntries.forEach((e) => {
          nextByKey[e.key] = (clamp(rawByKey[e.key]) / sumAll) * 100;
        });
      } else {
        serviceEntries.forEach((e, idx) => {
          nextByKey[e.key] = idx === 0 ? 100 : 0;
        });
      }
    } else {
      const nonEditedCostSum = serviceEntries.reduce((s, e) => {
        if (editedSet.has(e.key)) return s;
        return s + (Number(e.cost) || 0);
      }, 0);

      editedKeys.forEach((k) => {
        nextByKey[k] = clamp(rawByKey[k]);
      });

      serviceEntries.forEach((e) => {
        if (editedSet.has(e.key)) return;
        const share =
          nonEditedCostSum > 0 ? (Number(e.cost) || 0) / nonEditedCostSum : 0;
        nextByKey[e.key] = share * remaining;
      });
    }

    const orderedKeys = serviceEntries.map((e) => e.key);
    const round2 = (n) => Number((Number(n) || 0).toFixed(2));
    const rounded = {};
    orderedKeys.forEach((k) => {
      rounded[k] = round2(nextByKey[k]);
    });

    const sumRounded = orderedKeys.reduce((s, k) => s + (rounded[k] || 0), 0);
    const diff = round2(100 - sumRounded);
    const adjustKey =
      orderedKeys
        .slice()
        .reverse()
        .find((k) => !editedSet.has(k)) || orderedKeys[orderedKeys.length - 1];
    if (adjustKey) {
      rounded[adjustKey] = round2((rounded[adjustKey] || 0) + diff);
    }

    const prev2 = (k) => round2(parseFloat(latestMargins?.[k]));
    const changed = orderedKeys.some(
      (k) => !Number.isFinite(prev2(k)) || prev2(k) !== rounded[k]
    );
    if (!changed) return;

    setMarginValues((prev) => {
      const out = { ...(prev || {}) };
      orderedKeys.forEach((k) => {
        out[k] = rounded[k];
      });
      return out;
    });
  }, [groupedData, ratesReady, isProjectQuoted, editedMarginKeys, preserveMarginsOnRefresh]);

  useEffect(() => {
    recalculateTotals();
  }, [quotedValues, marginValues, recalculateTotals]);

  // Add this useEffect for cleanup
  useEffect(() => {
    return () => {
      if (marginValidationTimer) {
        clearTimeout(marginValidationTimer);
      }
    };
  }, [marginValidationTimer]);




  useEffect(() => {
    console.log("filteredQuotation", filteredQuotation)
    console.log("fafsasfgroupedData", groupedData)
  }, [filteredQuotation, groupedData]);




  const calculateQuotedValue = (typeService, rows, serviceGrandTotal) => {
    // Safety check - if rows is empty or undefined
    if (!rows || rows.length === 0 || !rows[0]) {
      return 0;
    }

    const marginKey = `${rows[0]?.ProjectID}-${typeService}`;

    // Use normalized margin so sum of Quoted Price per service = total quoted price (margins sum to 100.00)
    const marginPct = normalizedMarginsForCalc[marginKey] !== undefined
      ? normalizedMarginsForCalc[marginKey]
      : (marginValues[marginKey] !== undefined ? parseFloat(marginValues[marginKey]) || 0 : rows[0]?.servicewiseMargin || 0);

    const totalProjectQuotedValue = selectedProject?.QuotedValue || 0;

    // Quoted price per service type = total quoted price × Expected margin %
    const serviceQuotedValue = totalProjectQuotedValue > 0 ? (marginPct / 100) * totalProjectQuotedValue : 0;

    return serviceQuotedValue;
  };

  // Per-service-type quoted values as shown in the table (for Quotation modal / Send Email). Uses normalized margins so sum = total quoted price.
  const tableServiceTotals = useMemo(() => {
    const out = {};
    if (!groupedData || Object.keys(groupedData).length === 0) return out;
    const totalProjectQuotedValue = selectedProject?.QuotedValue || 0;
    Object.entries(groupedData).forEach(([typeService, rows]) => {
      if (!rows || rows.length === 0 || !rows[0]) {
        out[typeService] = 0;
        return;
      }
      const marginKey = `${rows[0]?.ProjectID}-${typeService}`;
      const marginPct = normalizedMarginsForCalc[marginKey] !== undefined
        ? normalizedMarginsForCalc[marginKey]
        : (marginValues[marginKey] !== undefined ? parseFloat(marginValues[marginKey]) || 0 : rows[0]?.servicewiseMargin || 0);
      out[typeService] = totalProjectQuotedValue > 0 ? (marginPct / 100) * totalProjectQuotedValue : 0;
    });
    return out;
  }, [groupedData, marginValues, normalizedMarginsForCalc, selectedProject?.QuotedValue]);

  useEffect(() => {
    // Strict: when quoted, totals are locked to backend quotation
    if (isProjectQuoted) return;
    if (typeof onTableServiceTotalsChange === "function") {
      onTableServiceTotalsChange(tableServiceTotals);
    }
  }, [tableServiceTotals, onTableServiceTotalsChange, isProjectQuoted]);

  // Update calculateGrossProfit with safety checks
  const calculateGrossProfit = (
    projectName,
    serviceGrandTotal,
    typeService,
    projectID,
    rows
  ) => {
    // Safety check
    if (!rows || rows.length === 0) {
      return 0;
    }

    const quotedValue = calculateQuotedValue(typeService, rows, serviceGrandTotal);
    const grossProfit = quotedValue - serviceGrandTotal;
    return grossProfit;
  };




  return (

    <>
      {isLoading ? <AdminLoader isLoading={isLoading} /> : <></>}

      <div className="pt-10">
          <div className="text-lg flex gap-2 font-semibold mb-2 pl-2">
            <h2 className="capitalize">
              Project Cost : {selectedProject.ProjectName}
            </h2 >
            <button
              onClick={handleClick}
              className="flex items-center space-x-2 focus:outline-none"
            >
              {/* Circle */}
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? "border-blue-600" : "border-gray-400"
                  }`}
              >
                {selected && (
                  <span className="w-3 h-3 bg-blue-600 rounded-full"></span>
                )}
              </span>

              {/* Label */}
              <span
                className={`text-base font-medium ${selected ? " font-semibold" : "text-gray-600"
                  }`}
              >
                Itemized P&amp;L
              </span>
            </button>
          </div >

          <div
            ref={tableContainerRef}
            style={{ scrollBehavior: "smooth" }}
            className="maintable  overflow-auto"
          >
            <div className=" flex text-xs font-semibold">
              <div className="flex-shrink-0 first-line:flex flex justify-start  align-middle items-center p-4  text-start w-52 bg-[#E8E8E8] ">
                Resources Designation
              </div>
              <div className="flex flex-shrink-0 justify-center  align-middle items-center p-4 border text-center w-40 bg-[#E8E8E8] ">
                Hours
              </div>
              <div className="flex flex-shrink-0 justify-center  align-middle items-center p-4 text-center  w-40 bg-[#E8E8E8] ">
                Hourly Rate({currency}/hr)
              </div>
              <div className="flex flex-col flex-shrink-0 justify-center  align-middle items-center p-4 text-center w-40 bg-[#E8E8E8]">
                Total Efforts({currency})
                <br />
                <p className="text-[12px]">(Hours * Hourly Rate)</p>
              </div>
              <div className="flex flex-shrink-0 justify-center  align-middle items-center p-4 text-center w-40 bg-[#E8E8E8]">
                Associated Cost({currency})
              </div>
              <div className="flex flex-col flex-shrink-0 justify-center  align-middle items-center p-3 text-center w-40 bg-[#E8E8E8] ">
                Total Cost({currency})
                <br />
                <p className="text-[12px]">(Total Efforts + Associated Cost)</p>
              </div>
              <div className="flex flex-shrink-0 justify-center  align-middle items-center p-4 text-center w-40 bg-[#E8E8E8] ">
                Total Cost Per Service({currency})
              </div>
              {selected && (
                <div className="flex ">
                  <div className="flex-shrink-0 w-40 p-4 gap-2 flex justify-center align-middle items-center text-center tracking-wider capitalize bg-[#E8E8E8]">
                    Expected margin(%)
                  </div>
                  <div className="flex-shrink-0 w-40 p-4 gap-2 flex justify-center align-middle items-center text-center tracking-wider capitalize bg-[#E8E8E8]">
                    Quoted Price({currency})
                  </div>

                  <div className="flex-shrink-0 w-40 p-4 flex justify-center align-middle items-center text-center tracking-wider capitalize bg-[#E8E8E8]">
                    Gross Profit({currency})
                  </div>
                  <div className="flex-shrink-0 w-40 p-4 flex justify-center align-middle items-center text-center tracking-wider capitalize bg-[#E8E8E8]">
                    G&A Expenses({currency})
                  </div>
                  {/* <div className="flex-shrink-0 w-24 p-4flex justify-center align-middle items-center text-center border border-black tracking-wider capitalize bg-[#1973eb] text-white">
                EBIT ({currency})
              </div> */}
                  <div className="flex-shrink-0 w-24 p-4 flex justify-center align-middle items-center text-center tracking-wider capitalize bg-[#E8E8E8]">
                    Tax({currency})
                  </div>
                  <div className="flex-shrink-0 w-52 flex flex-col justify-center align-middle items-center text-center tracking-wider capitalize bg-[#E8E8E8]">
                    <div className="h-[50%] w-full flex justify-center align-middle items-center">
                      {" "}
                      Net Revenue
                    </div>
                    <div className="h-[50%] flex w-full">
                      <div className="w-[50%] border-t border-r flex justify-center align-middle items-center">
                        ({currency})
                      </div>
                      <div className="w-[50%] border-t flex justify-center align-middle items-center">
                        (%)
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-28 p-4 flex justify-center align-middle items-center text-center text-xs tracking-wider capitalize bg-[#E8E8E8]">
                    Notes
                  </div>
                </div>
              )}
            </div>
            {/* <div className="Employee_Efforts_Calculation w-auto">
            {Object.entries(groupedData)
              .sort(([a], [b]) => {
                if (a?.toLowerCase().includes("hardware")) return 1;
                if (b?.toLowerCase().includes("hardware")) return -1;
                return 0;
              })
              .map(([typeService, rows], serviceIndex) => {
                const isHardware = typeService
                  ?.toLowerCase()
                  .includes("hardware");
                const hasManager = rows.some((row) => {
                  const role = row.roles[0]?.toLowerCase();
                  return (
                    role?.includes("manager") ||
                    role?.includes("project manager") ||
                    role?.includes("pm")
                  );
                });
                let serviceGrandTotal = rows.reduce((acc, row, idx) => {
                  let rowGrandTotal = Number(row.grandTotal) || 0;
                  if (isHardware && !hasManager && idx === 0) {
                    rowGrandTotal =
                      (Number(row.hardwareTotal) || 0) +
                      (Number(row.descriptionTotal) || 0);
                  }

                  return acc + rowGrandTotal;
                }, 0);
                return (
                  <div key={typeService} className="flex flex-col">
                    <div className={`flex text-xs border-b `}>
                      <div className="">
                        {(() => {
                          const groupedByManager = {};
                          let currentManager = null;
                          let managerCounter = 0;

                          rows.forEach((row) => {
                            const role = row.roles[0]?.toLowerCase();

                            if (
                              role?.includes("manager") ||
                              role?.includes("project manager") ||
                              role?.includes("pm")
                            ) {
                              managerCounter++;
                              currentManager = `manager${managerCounter}`;
                              groupedByManager[currentManager] = {
                                manager: row,
                                employees: [],
                              };
                            } else if (currentManager) {
                              groupedByManager[currentManager].employees.push(
                                row
                              );
                            } else {
                              if (!groupedByManager.default) {
                                groupedByManager.default = {
                                  manager: null,
                                  employees: [],
                                };
                              }
                              groupedByManager.default.employees.push(row);
                            }
                          });

                          const renderedRows = [];
                          Object.entries(groupedByManager).forEach(
                            ([managerKey, group]) => {
                              let employeeCounter = 0;
                              let operationsCounter = 0;

                              if (group.manager) {
                                const managerNumber = managerKey.replace(
                                  "manager",
                                  ""
                                );
                                renderedRows.push(
                                  <div
                                    key={`${managerKey}-manager`}
                                    className={`flex  `}
                                  >
                                    <div className="w-52 px-4 py-1 capitalize flex-shrink-0 flex justify-start items-center text-start">
                                      {`${typeService} Manager ${managerNumber}`}
                                    </div>
                                    <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                      {typeService
                                        ?.toLowerCase()
                                        .includes("hardware")
                                        ? ""
                                        : group.manager.totalHours}
                                    </div>
                                    <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                      {typeService
                                        ?.toLowerCase()
                                        .includes("hardware")
                                        ? ""
                                        : (ratesReady ? formatMoneyComma(group.manager.hourlyRate) : 0)}
                                    </div>
                                    <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-stacenterrt">
                                      {typeService
                                        ?.toLowerCase()
                                        .includes("hardware")
                                        ? USAComma(group.manager.hardwareTotal)
                                        : (ratesReady ? formatMoneyComma(group.manager.totalCost) : 0)}
                                    </div>
                                    <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                      {formatMoneyComma(group.manager.descriptionTotal)}
                                    </div>
                                    <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                      {formatMoneyComma(group.manager.grandTotal)}
                                    </div>
                                  </div>
                                );
                              }

                              group.employees.forEach((employee, idx) => {
                                const role = employee.roles[0]?.toLowerCase();
                                let displayText = "";

                                if (role === "operations") {
                                  operationsCounter++;
                                  displayText = `${typeService} Employee ${operationsCounter}`;
                                } else {
                                  employeeCounter++;
                                  displayText = `${typeService} Employee ${employeeCounter}`;
                                }

                                const isHardwareNoManager =
                                  typeService
                                    ?.toLowerCase()
                                    .includes("hardware") && !group.manager;
                                const isFirstEmployee = idx === 0;
                                const shouldShowHardwareValue =
                                  isHardwareNoManager && isFirstEmployee;

                                const calculatedGrandTotal =
                                  shouldShowHardwareValue
                                    ? (employee.hardwareTotal || 0) +
                                    (employee.descriptionTotal || 0)
                                    : employee.grandTotal;

                                renderedRows.push(
                                  <div
                                    key={`${managerKey}-employee-${idx}`}
                                    className={`flex `}
                                  >
                                    <div className="w-52 px-4 py-1 capitalize flex-shrink-0 flex justify-start items-center text-start ">
                                      {displayText}
                                    </div>
                                    <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center ">
                                      {typeService
                                        ?.toLowerCase()
                                        .includes("hardware")
                                        ? ""
                                        : employee.totalHours}
                                    </div>
                                    <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center ">
                                      {typeService
                                        ?.toLowerCase()
                                        .includes("hardware")
                                        ? ""
                                        : (ratesReady ? formatMoneyComma(employee.hourlyRate) : 0)}
                                    </div>
                                    <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                      {typeService
                                        ?.toLowerCase()
                                        .includes("hardware")
                                        ? shouldShowHardwareValue
                                          ? USAComma(employee.hardwareTotal)
                                          : ""
                                        : (ratesReady ? formatMoneyComma(employee.totalCost) : 0)}
                                    </div>
                                    <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                      {formatMoneyComma(employee.descriptionTotal)}
                                    </div>
                                    <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center ">
                                      {formatMoneyComma(calculatedGrandTotal)}
                                    </div>
                                  </div>
                                );
                              });
                            }
                          );

                          return renderedRows;
                        })()}
                      </div>
                      <div
                        className={`grandTotal w-40 py-1 px-4 flex-shrink-0 hidden md:flex justify-center items-center text-center border-b `}
                      >
                        {formatMoneyComma(serviceGrandTotal)}
                      </div>

                      {selected && (
                        <div className={`flex`}>
                          <div className="w-40 margin flex-shrink-0 px-3 py-2 text-wrap truncate border-b break-all flex justify-center items-center text-center">
                            <input
                              type="text"
                              readOnly={isProjectQuoted}
                              value={
                                marginValues[
                                  `${rows[0]?.ProjectID}-${typeService}`
                                ] !== undefined
                                  ? (() => {
                                    const v = marginValues[`${rows[0]?.ProjectID}-${typeService}`];
                                    const n = parseFloat(v);
                                    return isNaN(n) ? v : Number(n).toFixed(1);
                                  })()
                                  : rows[0]?.servicewiseMargin !== undefined && rows[0]?.servicewiseMargin !== null
                                    ? Number(rows[0].servicewiseMargin).toFixed(1)
                                    : ""
                              }
                              className={`margininput w-full h-full outline-none text-wrap break-all px-1 text-center border border-black rounded-md ${isProjectQuoted ? "bg-gray-100 cursor-default" : "bg-[#cfecf7]"}`}
                              onChange={(e) => {
                                if (isProjectQuoted) return;
                                let rawValue = e.target.value.replace(/[^0-9.]/g, "");
                                let parts = rawValue.split(".");
                                if (parts.length > 2) rawValue = parts[0] + "." + parts.slice(1).join("");
                                parts = rawValue.split(".");
                                if (parts[1] && parts[1].length > 1) rawValue = parts[0] + "." + parts[1].slice(0, 1);
                                handleMarginChange(
                                  typeService,
                                  rows[0],
                                  rows[0]?.ProjectID,
                                  rawValue,
                                  serviceGrandTotal
                                );
                              }}
                              onBlur={(e) => {
                                if (isProjectQuoted) return;
                                let rawValue = e.target.value.replace(/[^0-9.]/g, "");
                                const parts = rawValue.split(".");
                                if (parts[1] && parts[1].length > 1) rawValue = parts[0] + "." + parts[1].slice(0, 1);
                                const numericValue = parseFloat(rawValue) || 0;
                                const marginKey = `${rows[0]?.ProjectID}-${typeService}`;
                                setMarginValues((prev) => ({
                                  ...prev,
                                  [marginKey]: Number(numericValue).toFixed(1),
                                }));
                              }}

                              onFocus={(e) => {
                                if (isProjectQuoted) return;
                                const rawValue = e.target.value.replace(
                                  /[^0-9.]/g,
                                  ""
                                );
                                const marginKey = `${rows[0]?.ProjectID}-${typeService}`;
                                setMarginValues((prev) => ({
                                  ...prev,
                                  [marginKey]: rawValue,
                                }));
                              }}
                              placeholder="Enter Margin"
                            />
                          </div>

                          <div className="w-40 flex-shrink-0 px-3 py-2  text-wrap truncate break-all flex justify-center border-b items-center text-center">
                            {USAComma(calculateQuotedValue(typeService, rows, serviceGrandTotal))}
                          </div>


                          <div
                            className={`w-40 flex-shrink-0 py-1 px-4 flex justify-center items-center text-center text-wrap border-b truncate break-all
                  ${calculateGrossProfit(
                              selectedProject.ProjectName,
                              serviceGrandTotal,
                              typeService,
                              rows[0]?.ProjectID
                            ) < 0
                                ? "text-red-500"
                                : "text-black"
                              }`}
                          >
                            {USAComma(
                              calculateGrossProfit(
                                selectedProject.ProjectName,
                                serviceGrandTotal,
                                typeService,
                                rows[0]?.ProjectID
                              )
                            )}
                          </div>

                          <div
                            className={`w-40 flex-shrink-0 py-1 px-4  flex justify-center items-center text-center text-wrap border-b truncate break-all
                  ${calculateGrossProfit(
                              selectedProject.ProjectName,
                              serviceGrandTotal,
                              typeService,
                              rows[0]?.ProjectID
                            ) *
                                (GandATax / 100) <
                                0
                                ? "text-red-500"
                                : "text-black"
                              }`}
                          >
                            {USAComma(
                              calculateGrossProfit(
                                selectedProject.ProjectName,
                                serviceGrandTotal,
                                typeService,
                                rows[0]?.ProjectID
                              ) *
                              (GandATax / 100)
                            )}
                          </div>

                          <div
                            className={`w-24 flex-shrink-0 py-1 px-4 flex justify-center items-center text-center text-wrap border-b truncate break-all
                  ${calculateGrossProfit(
                              selectedProject.ProjectName,
                              serviceGrandTotal,
                              typeService,
                              rows[0]?.ProjectID
                            ) *
                                (taxValue / 100) <
                                0
                                ? "text-red-500"
                                : "text-black"
                              } `}
                          >
                            {USAComma(
                              calculateGrossProfit(
                                selectedProject.ProjectName,
                                serviceGrandTotal,
                                typeService,
                                rows[0]?.ProjectID
                              ) *
                              (taxValue / 100)
                            )}
                          </div>

                          <div className="w-52 flex-shrink-0  flex justify-center items-center text-center text-wrap truncate break-all border-b">
                            <div
                              className={`w-[50%] h-full  flex justify-center items-center text-center
                    ${calculateGrossProfit(
                                selectedProject.ProjectName,
                                serviceGrandTotal,
                                typeService,
                                rows[0]?.ProjectID
                              ) -
                                  calculateGrossProfit(
                                    selectedProject.ProjectName,
                                    serviceGrandTotal,
                                    typeService,
                                    rows[0]?.ProjectID
                                  ) *
                                  (GandATax / 100) -
                                  calculateGrossProfit(
                                    selectedProject.ProjectName,
                                    serviceGrandTotal,
                                    typeService,
                                    rows[0]?.ProjectID
                                  ) *
                                  (taxValue / 100) <
                                  0
                                  ? "text-red-500"
                                  : "text-black"
                                } `}
                            >
                              {USAComma(
                                calculateGrossProfit(
                                  selectedProject.ProjectName,
                                  serviceGrandTotal,
                                  typeService,
                                  rows[0]?.ProjectID
                                ) -
                                calculateGrossProfit(
                                  selectedProject.ProjectName,
                                  serviceGrandTotal,
                                  typeService,
                                  rows[0]?.ProjectID
                                ) *
                                (GandATax / 100) -
                                calculateGrossProfit(
                                  selectedProject.ProjectName,
                                  serviceGrandTotal,
                                  typeService,
                                  rows[0]?.ProjectID
                                ) *
                                (taxValue / 100)
                              )}
                            </div>
                            <div
                              className={`w-[50%] h-full  flex justify-center items-center text-center
                    ${(
                                  ((calculateGrossProfit(
                                    selectedProject.ProjectName,
                                    serviceGrandTotal,
                                    typeService,
                                    rows[0]?.ProjectID
                                  ) -
                                    calculateGrossProfit(
                                      selectedProject.ProjectName,
                                      serviceGrandTotal,
                                      typeService,
                                      rows[0]?.ProjectID
                                    ) *
                                    (GandATax / 100) -
                                    calculateGrossProfit(
                                      selectedProject.ProjectName,
                                      serviceGrandTotal,
                                      typeService,
                                      rows[0]?.ProjectID
                                    ) *
                                    (taxValue / 100)) /
                                    rows[0]?.ServiceQuotedValue) *
                                  100
                                ).toFixed(2) < 0
                                  ? "text-red-500"
                                  : (
                                    ((calculateGrossProfit(
                                      selectedProject.ProjectName,
                                      serviceGrandTotal,
                                      typeService,
                                      rows[0]?.ProjectID
                                    ) -
                                      calculateGrossProfit(
                                        selectedProject.ProjectName,
                                        serviceGrandTotal,
                                        typeService,
                                        rows[0]?.ProjectID
                                      ) *
                                      (GandATax / 100) -
                                      calculateGrossProfit(
                                        selectedProject.ProjectName,
                                        serviceGrandTotal,
                                        typeService,
                                        rows[0]?.ProjectID
                                      ) *
                                      (taxValue / 100)) /
                                      rows[0]?.ServiceQuotedValue) *
                                    100
                                  ).toFixed(2) < 10
                                    ? "text-orange-600"
                                    : "text-black"
                                } `}
                            >
                              {(
                                ((calculateGrossProfit(
                                  selectedProject.ProjectName,
                                  serviceGrandTotal,
                                  typeService,
                                  rows[0]?.ProjectID
                                ) -
                                  calculateGrossProfit(
                                    selectedProject.ProjectName,
                                    serviceGrandTotal,
                                    typeService,
                                    rows[0]?.ProjectID
                                  ) *
                                  (GandATax / 100) -
                                  calculateGrossProfit(
                                    selectedProject.ProjectName,
                                    serviceGrandTotal,
                                    typeService,
                                    rows[0]?.ProjectID
                                  ) *
                                  (taxValue / 100)) /
                                  rows[0]?.ServiceQuotedValue) *
                                100
                              )
                                .toFixed(2)}
                            </div>
                          </div>

                          <div className="w-28 flex-shrink-0 py-1 px-4  flex justify-center items-center text-center text-wrap border-b truncate break-all">
                            <textarea
                              className={`noteinput  w-24 h-full outline-none text-center break-words whitespace-normal resize-none overflow-x-hidden`}
                              value={
                                notes[`${rows[0]?.ProjectID}-${typeService}`] ??
                                rows[0]?.ProjectNotes ??
                                ""
                              }
                              onChange={(e) =>
                                handleNoteChange(
                                  typeService,
                                  rows[0]?.ProjectID,
                                  e.target.value
                                )
                              }
                              placeholder="Enter Notes"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div> */}

            <div className="Employee_Efforts_Calculation w-auto">
              {Object.entries(groupedData)
                .sort(([a], [b]) => {
                  if (a?.toLowerCase().includes("hardware")) return 1;
                  if (b?.toLowerCase().includes("hardware")) return -1;
                  return 0;
                })
                .map(([typeService, rows], serviceIndex) => {
                  // Check if hardware service and has any manager
                  const isHardware = typeService
                    ?.toLowerCase()
                    .includes("hardware");
                  const hasManager = rows.some((row) => {
                    const role = row.roles[0]?.toLowerCase();
                    return (
                      role?.includes("manager") ||
                      role?.includes("project manager") ||
                      role?.includes("pm")
                    );
                  });

                  let serviceGrandTotal = rows.reduce((acc, row, idx) => {
                    let rowGrandTotal = Number(row.grandTotal) || 0;
                    if (isHardware && !hasManager && idx === 0) {
                      rowGrandTotal =
                        (Number(row.hardwareTotal) || 0) +
                        (Number(row.descriptionTotal) || 0);
                    }

                    return acc + rowGrandTotal;
                  }, 0);

                  return (
                    <div key={typeService} className="flex flex-col">
                      <div className={`flex text-xs border-b `}>
                        <div className="">
                          {/* Group and render rows with managers first */}
                          {(() => {
                            // Separate managers and employees
                            const managers = rows.filter(row => {
                              const role = row.roles[0]?.toLowerCase();
                              return (
                                role?.includes("manager") ||
                                role?.includes("project manager") ||
                                role?.includes("pm")
                              );
                            });

                            const employees = rows.filter(row => {
                              const role = row.roles[0]?.toLowerCase();
                              return !(
                                role?.includes("manager") ||
                                role?.includes("project manager") ||
                                role?.includes("pm")
                              );
                            });

                            // Track counters for display
                            let managerCounter = 0;
                            let employeeCounter = 0;
                            let operationsCounter = 0;

                            return (
                              <>
                                {/* Render all managers first */}
                                {managers.map((manager) => {
                                  managerCounter++;
                                  return (
                                    <div
                                      key={`manager-${managerCounter}`}
                                      className={`flex`}
                                    >
                                      <div className="w-52 px-4 py-1 capitalize flex-shrink-0 flex justify-start items-center text-start ">
                                        {`${typeService} Manager ${managerCounter}`}
                                      </div>
                                      <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center ">
                                        {typeService
                                          ?.toLowerCase()
                                          .includes("hardware")
                                          ? ""
                                          : formatHours(manager.totalHours)}
                                      </div>
                                      <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center ">
                                        {typeService
                                          ?.toLowerCase()
                                          .includes("hardware")
                                          ? ""
                                          : (ratesReady ? formatMoneyComma(manager.hourlyRate) : 0)}
                                      </div>
                                      <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-stacenterrt ">
                                        {typeService
                                          ?.toLowerCase()
                                          .includes("hardware")
                                          ? USAComma(manager.hardwareTotal)
                                          : (ratesReady ? formatMoneyComma(manager.totalCost) : 0)}
                                      </div>
                                      <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center ">
                                        {formatMoneyComma(manager.descriptionTotal)}
                                      </div>
                                      <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center ">
                                        {formatMoneyComma(manager.grandTotal)}
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* Render all employees after managers */}
                                {employees.map((employee, empIndex) => {
                                  const role = employee.roles[0]?.toLowerCase();
                                  let displayText = "";

                                  if (role === "operations") {
                                    operationsCounter++;
                                    displayText = `${typeService} Employee ${operationsCounter}`;
                                  } else {
                                    employeeCounter++;
                                    displayText = `${typeService} Employee ${employeeCounter}`;
                                  }

                                  // Check if hardware service, no manager, and this is the first employee
                                  const isHardwareNoManager =
                                    typeService?.toLowerCase().includes("hardware") &&
                                    managers.length === 0;
                                  const isFirstEmployee = empIndex === 0;
                                  const shouldShowHardwareValue = isHardwareNoManager && isFirstEmployee;

                                  // Calculate grandTotal: if hardware, no manager, and first employee, include hardwareTotal
                                  const calculatedGrandTotal = shouldShowHardwareValue
                                    ? (employee.hardwareTotal || 0) + (employee.descriptionTotal || 0)
                                    : employee.grandTotal;

                                  return (
                                    <div
                                      key={`employee-${empIndex}`}
                                      className={`flex`}
                                    >
                                      <div className="w-52 px-4 py-1 capitalize flex-shrink-0 flex justify-start items-center text-start">
                                        {displayText}
                                      </div>
                                      <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                        {typeService
                                          ?.toLowerCase()
                                          .includes("hardware")
                                          ? ""
                                          : formatHours(employee.totalHours)}
                                      </div>
                                      <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                        {typeService
                                          ?.toLowerCase()
                                          .includes("hardware")
                                          ? ""
                                          : (ratesReady ? formatMoneyComma(employee.hourlyRate) : 0)}
                                      </div>
                                      <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                        {typeService
                                          ?.toLowerCase()
                                          .includes("hardware")
                                          ? shouldShowHardwareValue
                                            ? USAComma(employee.hardwareTotal)
                                            : ""
                                          : (ratesReady ? formatMoneyComma(employee.totalCost) : 0)}
                                      </div>
                                      <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                        {formatMoneyComma(employee.descriptionTotal)}
                                      </div>
                                      <div className="w-40 px-4 py-1 flex-shrink-0 flex justify-center items-center text-center">
                                        {formatMoneyComma(calculatedGrandTotal)}
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            );
                          })()}
                        </div>

                        {/* 👉 Service Grand Total column (visible on larger screens) */}
                        <div
                          className={`grandTotal w-40 py-1 px-4 flex-shrink-0 hidden md:flex justify-center items-center text-center border-b `}
                        >
                          {formatMoneyComma(serviceGrandTotal)}
                        </div>

                        {selected && (
                          <div className={`flex`}>
                            {/* Margin Input: read-only when project is quoted (isProjectQuoted: true), show saved margin from DB only */}
                            <div className="w-40 margin flex-shrink-0 px-3 py-2 text-wrap truncate border-b break-all flex justify-center items-center text-center">
                              <input
                                type="text"
                                readOnly={quotedproject?.length > 0}
                                value={
                                  marginValues[
                                    `${rows[0]?.ProjectID}-${typeService}`
                                  ] !== undefined
                                    ? marginValues[
                                    `${rows[0]?.ProjectID}-${typeService}`
                                    ]
                                    : rows[0]?.servicewiseMargin !== undefined && rows[0]?.servicewiseMargin !== null
                                      ? Number(rows[0].servicewiseMargin).toFixed(2)
                                      : ""
                                }
                                className={`margininput w-full h-full outline-none text-wrap break-all px-1 text-center border border-black rounded-md ${quotedproject?.length > 0 ? "bg-gray-100 cursor-default" : "bg-[#cfecf7]"}`}
                                onChange={(e) => {
                                  if (quotedproject?.length > 0) return;
                                  const rawValue = e.target.value.replace(
                                    /[^0-9.]/g,
                                    ""
                                  );
                                  handleMarginChange(
                                    typeService,
                                    rows[0],
                                    rows[0]?.ProjectID,
                                    rawValue,
                                    serviceGrandTotal
                                  );
                                }}
                                onBlur={(e) => {
                                  if (quotedproject?.length > 0) return;
                                  const rawValue = e.target.value.replace(
                                    /[^0-9.]/g,
                                    ""
                                  );
                                  const numericValue = parseFloat(rawValue) || 0;
                                  const marginKey = `${rows[0]?.ProjectID}-${typeService}`;
                                  setMarginValues((prev) => ({
                                    ...prev,
                                    [marginKey]: Number(numericValue).toFixed(2),
                                  }));
                                }}
                                onFocus={(e) => {
                                  if (quotedproject?.length > 0) return;
                                  const rawValue = e.target.value.replace(
                                    /[^0-9.]/g,
                                    ""
                                  );
                                  const marginKey = `${rows[0]?.ProjectID}-${typeService}`;
                                  setMarginValues((prev) => ({
                                    ...prev,
                                    [marginKey]: rawValue,
                                  }));
                                }}
                                placeholder="Enter Margin"
                              />
                            </div>

                            {/* Quoted Price = (Margin% / 100) × Total Quoted Value */}
                            <div className="w-40 flex-shrink-0 px-3 py-2  text-wrap truncate break-all flex justify-center border-b items-center text-center">
                              {USAComma(calculateQuotedValue(typeService, rows, serviceGrandTotal))}
                            </div>

                            {/* Gross Profit */}
                            <div
                              className={`w-40 flex-shrink-0 py-1 px-4 flex justify-center items-center text-center text-wrap border-b truncate break-all
                    ${calculateGrossProfit(
                                selectedProject.ProjectName,
                                serviceGrandTotal,
                                typeService,
                                rows[0]?.ProjectID,
                                rows
                              ) < 0
                                  ? "text-red-500"
                                  : "text-black"
                                }`}
                            >
                              {USAComma(
                                calculateGrossProfit(
                                  selectedProject.ProjectName,
                                  serviceGrandTotal,
                                  typeService,
                                  rows[0]?.ProjectID,
                                  rows
                                )
                              )}
                            </div>

                            {/* G&A Expenses — use itemized value so per-service sum matches total (fixes off-by-one rounding) */}
                            <div
                              className={`w-40 flex-shrink-0 py-1 px-4  flex justify-center items-center text-center text-wrap border-b truncate break-all
                    ${(itemizedGAByService[typeService] ?? calculateGrossProfit(
                                selectedProject.ProjectName,
                                serviceGrandTotal,
                                typeService,
                                rows[0]?.ProjectID,
                                rows
                              ) * (GandATax / 100)) < 0
                                  ? "text-red-500"
                                  : "text-black"
                                }`}
                            >
                              {USAComma(
                                itemizedGAByService[typeService] ??
                                calculateGrossProfit(
                                  selectedProject.ProjectName,
                                  serviceGrandTotal,
                                  typeService,
                                  rows[0]?.ProjectID,
                                  rows
                                ) * (GandATax / 100)
                              )}
                            </div>

                            {/* Tax — use itemized rounded value so row sum matches total (fixes 1₹ drift) */}
                            <div
                              className={`w-24 flex-shrink-0 py-1 px-4 flex justify-center items-center text-center text-wrap border-b truncate break-all 
                    ${(itemizedTaxByService[typeService] ?? calculateGrossProfit(
                                selectedProject.ProjectName,
                                serviceGrandTotal,
                                typeService,
                                rows[0]?.ProjectID,
                                rows
                              ) * (taxValue / 100)) < 0
                                  ? "text-red-500"
                                  : "text-black"
                                } `}
                            >
                              {USAComma(
                                itemizedTaxByService[typeService] ?? Math.round(
                                  calculateGrossProfit(
                                    selectedProject.ProjectName,
                                    serviceGrandTotal,
                                    typeService,
                                    rows[0]?.ProjectID,
                                    rows
                                  ) * (taxValue / 100)
                                )
                              )}
                            </div>

                            {/* Net Revenue — use itemized rounded value so row sum matches total (fixes 1₹ drift from Expected margin % decimals) */}
                            <div className="w-52 flex-shrink-0  flex justify-center items-center text-center text-wrap truncate break-all border-b">
                              {(() => {
                                const netRevenueAmt = itemizedNetRevenueByService[typeService] ?? Math.round(
                                  calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID, rows) -
                                  (itemizedGAByService[typeService] ?? calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID, rows) * (GandATax / 100)) -
                                  (itemizedTaxByService[typeService] ?? calculateGrossProfit(selectedProject.ProjectName, serviceGrandTotal, typeService, rows[0]?.ProjectID, rows) * (taxValue / 100))
                                );
                                const quotedPriceForService = calculateQuotedValue(typeService, rows, serviceGrandTotal) || 1;
                                const netRevPct = quotedPriceForService > 0 ? (netRevenueAmt / quotedPriceForService) * 100 : 0;
                                return (
                                  <>
                                    <div
                                      className={`w-[50%] h-full  flex justify-center items-center text-center 
                                        ${netRevenueAmt < 0 ? "text-red-500" : "text-black"}`}
                                    >
                                      {USAComma(netRevenueAmt)}
                                    </div>
                                    <div
                                      className={`w-[50%] h-full  flex justify-center items-center text-center 
                                        ${netRevPct < 0 ? "text-red-500" : netRevPct < 10 ? "text-orange-600" : "text-black"}`}
                                    >
                                      {netRevPct.toFixed(2)}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>

                            {/* Notes Input */}
                            <div className="w-28 flex-shrink-0 py-1 px-4  flex justify-center items-center text-center text-wrap border-b truncate break-all">
                              <textarea
                                className={`noteinput  w-24 h-full outline-none text-center break-words whitespace-normal resize-none overflow-x-hidden`}
                                value={
                                  notes[`${rows[0]?.ProjectID}-${typeService}`] ??
                                  rows[0]?.ProjectNotes ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleNoteChange(
                                    typeService,
                                    rows[0]?.ProjectID,
                                    e.target.value
                                  )
                                }
                                placeholder="Enter Notes"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="Totaltable flex w-full text-start text-sm font-semibold ">
              <div className="flex-shrink-0  py-1 px-4  text-start  w-52 border-b">
                Total
              </div>

              <div className="flex-shrink-0 py-1 px-4 text-center w-40 border-b">
                {formatHours(totalHoursSum)}
              </div>
              <div className="flex-shrink-0 py-1 px-4 text-center w-40 border-b">
                <div className="flex justify-around items-center"></div>
              </div>
              <div className="flex-shrink-0 py-1 px-4 text-center w-40 border-b">
                {formatMoneyComma(totalCostSum + matchedHardware)}
              </div>
              <div className="descriptionvaluetotal  flex-shrink-0 py-1 px-4 text-center border-b w-40">
                {formatMoneyComma(matchedDescription)}
              </div>
              <div className="alltotal flex-shrink-0 py-1 px-4 text-center w-40 border-b ">
                {formatMoneyComma(totalServiceGrandTotal)}
              </div>
              <div className="sumOFserviceGrandTotal flex-shrink-0 py-1 px-4 text-center w-40 border-b">
                {USAComma(Math.round(Number(totalServiceGrandTotal) || 0))}
              </div>
              {selected && (
                <div className="flex border-b">
                  <div className={`allmargintotal flex-shrink-0 py-1 px-4 text-center w-40 border-b ${(Number(displayTotalMargin) !== 100) ? "text-red-500 font-bold" : ""}`}>
                    {displayTotalMargin}
                    {/* {USAComma(
                  Object.values(groupedData).reduce((total, rows) => {
                    const marginKey = `${rows[0]?.ProjectID}-${rows[0]?.typeService}`;
                    const marginValue = marginValues[marginKey] !== undefined
                      ? parseFloat(marginValues[marginKey]) || 0
                      : rows[0]?.servicewiseMargin || 0;
                    return total + marginValue;
                  }, 0)
                )} */}
                  </div>
                  <div className="alltotal flex-shrink-0 py-1 px-4 text-center w-40 border-b">
                    {USAComma(
                      selectedProject?.QuotedValue != null && selectedProject?.QuotedValue !== ""
                        ? Number(selectedProject.QuotedValue)
                        : Object.values(groupedData).reduce((total, rows) => {
                            const serviceKey = `${rows[0]?.ProjectID}-${rows[0]?.typeService}`;
                            const quotedValue =
                              quotedValues[serviceKey] !== undefined
                                ? parseFloat(quotedValues[serviceKey]) || 0
                                : rows[0]?.ServiceQuotedValue || 0;
                            return total + quotedValue;
                          }, 0)
                    )}
                  </div>
                  <div
                    className={`sumofGrossProfit flex-shrink-0 py-1 px-4  text-center w-40 border-b  ${totalGrossProfit < 0 ? "text-red-500" : "text-black"
                      }`}
                  >
                    {USAComma(totalGrossProfit)}
                  </div>
                  <div
                    className={`sumofGAExpenses flex-shrink-0 py-1 px-4  text-center w-40 border-b ${totalGAExpenses < 0 ? "text-red-500" : "text-black"
                      }`}
                  >
                    {USAComma(totalGAExpenses)}
                  </div>
                  {/* <div className={`sumofEBIT flex-shrink-0 p-1 border border-black text-center w-24 bg-[#e7ebfb] ${totalEBIT < 0 ? "text-red-500" : "text-black"
                }`}>
                {USAComma(totalEBIT)}
              </div> */}
                  <div
                    className={`sumofTax flex-shrink-0 py-1 px-4 text-center w-24  border-b ${totalTax < 0 ? "text-red-500" : "text-black"
                      }`}
                  >
                    {USAComma(totalTax)}
                  </div>
                  <div className="sumofNetRevenue flex flex-shrink-0   text-center w-52 border-b">
                    <div
                      className={`w-[50%] h-full  flex justify-center items-center text-center ${totalNetRevenue < 0 ? "text-red-500" : "text-black"
                        }`}
                    >
                      {USAComma(totalNetRevenue)}
                    </div>
                    <div
                      className={`w-[50%] h-full flex justify-center  border-b items-center text-center ${netRevenuePercentage < 0
                        ? "text-red-500"
                        : netRevenuePercentage < 10
                          ? "text-orange-600"
                          : "text-black"
                        }`}
                    >
                      {netRevenuePercentage.toFixed(2)}
                    </div>
                  </div>
                  <div className="alltotal flex-shrink-0 py-1 px-4  text-center w-24 border-b "></div>
                </div>
              )}
            </div>
          </div>
        </div>

    </>

  );
});

AllServiceTable.displayName = "AllServiceTable";

export default AllServiceTable;